// Typed wrappers over the broker SURETY BOND BFF (`/api/broker/bond/**`), the
// seam behind the config-driven bond intake flow (BRK-E005). Same shape as the
// other broker seams (brokerAccounts / brokerSubmissions / brokerProducts): thin
// functions over the authed requester, so they inherit the access-token injection
// and silent refresh-on-401 (BRK-S015), with types derived from the PMS contract
// rather than hand-maintained in parallel (NN #3).
//
// Topology: this SPA → PMS broker BFF → discovery service → Lendflow. Bond data
// lives in discovery; PMS keeps a thin pointer table and mints the real
// **SB-#####** number. A pointer exists from DRAFT START, so an order the broker
// is still filling in already appears in the list.
//
// CONTRACT STATUS (2026-08-11). Only `GET /api/broker/bond/submissions` is live
// in `pms-local docs/openapi.yaml` — its types below are derived from it
// verbatim. The catalog / config / create / step / run / submit routes are still
// being built server-side (PMS-S183 et al), so those types are built against the
// contract pinned across the three repos for this initiative. If PMS ships
// something different, **the PMS openapi wins**: update these types, the dev
// mock, and `test/lib/brokerBondsContract.test.ts` together — see the drift
// guard's header for why that guard exists.
import { ApiError, apiErrorCode, type FileResponse } from "@/lib/api";
import type {
  AuthedBlobRequester,
  AuthedProgressUploader,
  AuthedRequester,
  AuthedUploader,
} from "@/lib/brokerAuth";
import type { BondEligibilityBlock } from "@/lib/bondEligibility";
import { DOCUMENT_RULES, type DocumentRule } from "@/lib/brokerDocumentTypes";
import type { Page } from "@/lib/brokerAccounts";
import type { BondGiaSummary } from "@/lib/brokerGia";

const BASE = "/api/broker/bond";

// ─── Catalog (products → subproducts) ─────────────────────────────────────────

/**
 * Which SHAPE a bond order's flow has (PMS-S434, adopted in BRK-S160).
 *
 * `bond` is every order this portal has ever filed: eligibility, the
 * configuration's steps, and the code-owned project-&-bond terms. `preapproval`
 * is the STANDALONE pre-qualification — the same steps, no terms, and the
 * Indication chip as the terminal screen.
 *
 * OPTIONAL wherever it is read, and the absence means `bond`: a configuration
 * written before the key existed, and a PMS that predates it, are the same fact
 * and neither may change what a bond order does. `bondFlowOf` is the ONE place
 * that default lives, so no surface can spell it differently.
 */
export type BondFlow = "bond" | "preapproval";

/** The flow a config / order / list row declares, defaulting to `bond`. */
export function bondFlowOf(
  source: { flow?: BondFlow } | null | undefined,
): BondFlow {
  return source?.flow ?? "bond";
}

/**
 * The catalog `key` of the subproduct that runs the STANDALONE pre-approval.
 *
 * The ONE place in this portal that names a product. The wizard branches on the
 * resolved configuration's `flow` and on nothing product-specific — this
 * constant exists only so the account's "Get pre-approved" CTA can find which
 * subproduct to send the broker to, which is a question about the catalog
 * rather than about the flow. If the catalog carries no such subproduct the CTA
 * falls back to today's link, and nothing else changes.
 */
export const PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY = "program_preapproval";

/** A bond subproduct — what the broker actually picks at step 1 (e.g. Bid Bond).
 *  A resolved config attaches at product OR subproduct level; this portal only
 *  ever sees the RESOLVED config, so inheritance is invisible here. */
export interface BondSubproduct {
  id: string;
  name: string;
  /** A short line of helper text for the picker, when the catalog supplies one. */
  description?: string | null;
  /**
   * PMS's stable slug for this subproduct (BRK-S160).
   *
   * The catalog has published it all along and the picker deliberately ignored
   * it — a broker chooses a product by NAME, and the id is what addresses it.
   * It is read now for exactly one thing: finding the standalone pre-approval
   * subproduct from the account CTA (`PROGRAM_PREAPPROVAL_SUBPRODUCT_KEY`).
   * OPTIONAL here so a catalog read that omits it degrades to no CTA rather
   * than to a broken link.
   */
  key?: string;
}

/** A bond product (e.g. Construction Bond) with its selectable subproducts. */
export interface BondProduct {
  id: string;
  name: string;
  description?: string | null;
  subproducts: BondSubproduct[];
}

/** GET /api/broker/bond/catalog — the bond products the brokerage may submit. */
export function getBondCatalog(
  request: AuthedRequester,
): Promise<{ items: BondProduct[] }> {
  return request<{ items: BondProduct[] }>(`${BASE}/catalog`);
}

// ─── Resolved product configuration ───────────────────────────────────────────

/**
 * One choice on a `select`. A plain STRING means the value IS the label — the
 * original spelling, kept first-class so a config can add a choice without a
 * client release, and so every configuration written before SUR-S057 (and every
 * in-flight draft snapshot) keeps working untouched.
 *
 * A PAIR carries them separately, for a field whose value is an identifier the
 * broker must never see — `business.entityType` is a Lendflow option id, where
 * `1` means LLC. Mirrors the widened contract VERBATIM; discovery owns it.
 */
export type BondFieldOption =
  | string
  | { value: string | number; label: string };

/** A field on a config-driven step. `path` addresses where the value lives in the
 *  bond order's value bag (`personal[].firstName` addresses a repeatable group's
 *  member), which is why it is a path rather than a flat key. */
export interface BondConfigField {
  path: string;
  label: string;
  type?: BondFieldType;
  required?: boolean;
  /** Options for a `select`; ignored for every other type. */
  options?: BondFieldOption[];
  /**
   * Which VALUES are answers, as distinct from what the control looks like — a
   * date input takes any date, `dateOfBirth` says which dates qualify
   * (SUR-S041). Enforced server-side too; validating against it here only means
   * the broker learns before the round-trip, never that this is the only gate.
   */
  format?: BondFieldFormat;
  /** NUMERIC bounds only — a date's rule rides on `format`, not on min/max. */
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  /** `files` ONLY (BRK-S145) — see `BondFinalField`, where the same two keys
   *  carry the same meaning. Declared on both because a configuration may put a
   *  `files` field on either surface and the renderer reads one adapter type. */
  maxFiles?: number;
  accept?: string[];
  /**
   * The field's value is PMS's rather than the vendor's (SUR-S161, adopted in
   * BRK-S172 ahead of the pin).
   *
   * **READ BY NOTHING, AND THAT IS THE POINT.** It says where the value goes
   * UPSTREAM, which is discovery's business; it says nothing about how the
   * field is drawn. A local field renders with its declared control exactly
   * like any other, and the moment this client branched on the flag it would be
   * deciding a question that is not its to decide — a broker would be looking
   * at a differently-shaped box because of where the answer is filed.
   *
   * Declared all the same, so the fact is stated in the type rather than left
   * for the next person to rediscover, and so a test can assert it changes
   * nothing. Recorded in `AHEAD_OF_PIN` and expires at the re-pin.
   */
  local?: boolean;
}

/** The field types a config may ask for. `address` and `phone` render the shared
 *  composite controls (AddressFieldset / PhoneField) rather than a bare input;
 *  `files` renders the drop zone (BRK-S145). */
export type BondFieldType =
  | "text"
  | "textarea"
  | "currency"
  | "date"
  | "number"
  | "percent"
  | "select"
  | "checkbox"
  | "address"
  | "phone"
  /**
   * ADOPTED AHEAD OF THE PIN (BRK-S145, PMS-S333). A field whose answer is
   * UPLOADED DOCUMENTS rather than a typed value: the control is a drop zone,
   * and the value in the step bag is the list of references PMS returned for
   * the files attached to it (`BondDocumentRef[]`).
   *
   * Adopted before the contract publishes it for the reason every widening into
   * a closed reader is: `fieldKind` classifies an unknown `type` as
   * `unsupported` and renders a guarded card that BLOCKS the step, which is the
   * right answer for a control this bundle cannot draw and the wrong one for a
   * control it can. So the reader has to know the member before the first
   * configuration carries it — deploy order PMS → BRK → SUR, consumers first.
   * See `AHEAD_OF_PIN` in test/contract/bondSeamMirror.ts, which is what keeps
   * that a window rather than a hole.
   */
  | "files";

/**
 * A named validation rule the control type does not imply (SUR-S041's
 * `FIELD_FORMATS`). Mirrors discovery's frozen list VERBATIM — the vocabulary is
 * owned there, so a new member is a mirror update here, never a local invention.
 */
export type BondFieldFormat =
  | "email"
  | "ssn"
  | "ein"
  /** Exactly nine digits, no checksum — the Canadian BN and SIN. */
  | "nineDigits"
  /** Two-letter US state code. */
  | "state"
  /** A date at least 18 years in the past, and no earlier than 1900. */
  | "dateOfBirth"
  /** A date not in the future. */
  | "pastDate";

/**
 * A ROSTER a fields step collects, rather than one set of answers (PMS-S341,
 * adopted in BRK-S152).
 *
 * A CLOSED union with one member today, and a separate axis from `type` for the
 * reason `BondFieldsStep` already has one: the step is still a `fields` step —
 * same controls, same value bag, same save — and what changes is how many times
 * its repeated block is drawn. Folding it into `type` would make every existing
 * `fields` branch stop matching the moment a configuration declared one.
 */
export type BondRosterKind = "indemnity";

/** What a step's `roster` can be classified as. `"none"` is the ordinary step
 *  (no roster declared); `"unsupported"` is a roster the CONTRACT publishes and
 *  this BUNDLE cannot draw — never a contract member. */
export type BondRosterClass = BondRosterKind | "none" | "unsupported";

const RENDERED_ROSTERS = ["indemnity"] as const;

/** A step the broker fills in. Config-driven: the flow's middle is whatever the
 *  admin-managed configuration says it is. */
export interface BondFieldsStep {
  key: string;
  title: string;
  type: "fields";
  fields: BondConfigField[];
  /**
   * ADOPTED AHEAD OF THE PIN (BRK-S152, PMS-S393 / SUR-S159). Declares that this
   * step's repeated block is a ROSTER — one block per person — rather than the
   * single set of answers every `fields` step has drawn until now.
   *
   * Absent is the ordinary step and always will be: most fields steps capture
   * the order, not a list of people.
   */
  roster?: BondRosterKind;
}

/**
 * Which roster a fields step declares — `"unsupported"` for a value this bundle
 * cannot draw.
 *
 * THE ONE PLACE A ROSTER IS CLASSIFIED, and it exists for the reason `stepKind`
 * and `fieldKind` do: the union above is CLOSED and the contract's vocabulary
 * grows, so a widened `roster` arrives as a value the type system swears cannot
 * exist and every `=== "indemnity"` check silently returns false for it. The
 * step would then render as an ORDINARY fields step — one block where the
 * configuration asked for a roster — with a live Continue in front of it and
 * nothing on screen saying a roster was asked for. That is the BRK-S114 shape
 * exactly, so an unrecognised roster is named here and the step renders the
 * guarded card instead.
 */
export function rosterKind(step: BondStep): BondRosterClass {
  if (stepKind(step) !== "fields") return "none";
  const declared: unknown = (step as BondFieldsStep).roster;
  if (declared === undefined || declared === null) return "none";
  if (typeof declared !== "string") return "unsupported";
  const known = RENDERED_ROSTERS.find((r) => r === declared);
  return known ?? "unsupported";
}

/** A step that pulls data from a bureau via a Lendflow orchestration run. The
 *  broker answers nothing — it runs, settles, and its outcome may flag the order
 *  for underwriting review without ever blocking it. */
export interface BondDataPullStep {
  key: string;
  title: string;
  type: "data_pull";
  /**
   * BOTH spellings, both OPTIONAL — the shape the contract actually publishes
   * (BRK-S097).
   *
   * The singular is not deprecated: every configuration written before SUR-S051
   * uses it, including the config SNAPSHOT inside a draft a broker is halfway
   * through. The plural is what a genuinely multi-orchestration step carries.
   * This field used to be declared as a REQUIRED singular, which is the latent
   * break that cost PMS-S221 one layer up — nothing existing changes, and the
   * first configuration written with only the plural fails the read.
   */
  orchestrationTemplateId?: string;
  orchestrationTemplateIds?: string[];
}

/** Every orchestration a data-pull step runs, in either spelling. One list, so
 *  no caller has to know which spelling a configuration used. */
export function stepOrchestrations(step: BondDataPullStep): string[] {
  return (
    step.orchestrationTemplateIds ??
    (step.orchestrationTemplateId === undefined
      ? []
      : [step.orchestrationTemplateId])
  );
}

/**
 * The PREAPPROVAL step (PMS-S306 / BRK-S132) — one configuration step that the
 * wizard renders as TWO chips: **Checks**, then **Indication**.
 *
 * It carries NO orchestration ids, and that is the whole shape difference from
 * `data_pull`: which bureaus a program preapproval consults is Signal's
 * underwriting business, not the broker's, and a step that named them would put
 * them on a broker's screen the moment somebody rendered the field. The run is
 * driven through the SAME endpoints a data pull uses — the step key is all
 * either of them needs — so the seam gains no route here.
 *
 * ADOPTED AHEAD OF THE PIN, and ahead of discovery emitting it (deploy order
 * PMS → BRK → SUR). BRK's step reader is a discriminated union, so an unadopted
 * `type` is a step the wizard cannot render: it has to know this member before
 * the first configuration carries it. See `bondChips`, which is where one step
 * becoming two chips lives, and `BondUnsupportedStep` for what an unknown type
 * still does.
 */
export interface BondPreapprovalStep {
  key: string;
  title: string;
  type: "preapproval";
}

export type BondStep = BondFieldsStep | BondDataPullStep | BondPreapprovalStep;

/** What a step's `type` can be classified as. `"unsupported"` is not a contract
 *  member — it is the answer for a `type` the CONTRACT publishes and this
 *  BUNDLE has never heard of. */
export type BondStepKind =
  | "fields"
  | "data_pull"
  | "preapproval"
  | "unsupported";

/** The kinds this build renders. As a value, so `stepKind` cannot disagree with
 *  the union above. */
const RENDERED_STEP_TYPES = ["fields", "data_pull", "preapproval"] as const;

/**
 * Which kind a step is — `"unsupported"` for a `type` this bundle cannot
 * render.
 *
 * THE ONE PLACE A STEP'S TYPE IS CLASSIFIED, and the reason it exists rather
 * than a `switch (step.type)` at each site: the union above is CLOSED, and the
 * contract's vocabulary grows. A widened `type` therefore arrives as a value
 * the type system swears cannot exist, and every `step.type === "…"` check
 * simply returns false for it — silently, at every site, with no `default:`
 * anyone forgot to write. That is precisely how the wizard shipped a step that
 * rendered NOTHING with a live footer Continue in front of it: both branches
 * fell through and nothing said so.
 *
 * So this reads the tag as the STRING it really is, and the wizard renders the
 * leftovers as a named, guarded "this step needs a newer version" card with a
 * way forward (`UnsupportedStep`).
 */
export function stepKind(step: BondStep): BondStepKind {
  const tag: string = step.type;
  const known = RENDERED_STEP_TYPES.find((t) => t === tag);
  return known ?? "unsupported";
}

// Narrowing predicates built on `stepKind`, so a caller gets the step's real
// type without an `as` cast (NN #7) and without re-stating the tag comparison.
// The runtime check and the narrowing are then the same decision.

export function isFieldsStep(step: BondStep): step is BondFieldsStep {
  return stepKind(step) === "fields";
}

export function isDataPullStep(step: BondStep): step is BondDataPullStep {
  return stepKind(step) === "data_pull";
}

export function isPreapprovalStep(step: BondStep): step is BondPreapprovalStep {
  return stepKind(step) === "preapproval";
}

/** The code-owned final step: the project & bond terms. Its FIELDS come from
 *  config; the computed read-only displays (bid-bond penalty, contract duration,
 *  maintenance amount) stay code-owned and key off well-known field keys. */
export interface BondFinalStep {
  fields: BondFinalField[];
}

/** A final-step field. Keyed (not pathed) — the final step is a flat bag. */
export interface BondFinalField {
  key: string;
  label: string;
  type: BondFieldType;
  required?: boolean;
  /**
   * Plain strings ONLY — deliberately NOT widened alongside the fields-step's
   * `options` (SUR-S057 / PMS-S227). Discovery's final-step schema emits
   * `string[]`, and PMS-S186 corrected this very spec BECAUSE it once documented
   * pairs here that discovery cannot send. The fields step needed pairs only
   * because Lendflow's option IDs are numeric; a bond-order select's values are
   * its own labels. Do not "harmonize" the two — if discovery widens its
   * final-step schema, mirror it then.
   */
  options?: string[];
  /** Helper text rendered under the input. Spelled `helpText` because that is
   *  what the contract publishes — BRK read `help` until BRK-S097, a field PMS
   *  has never sent, so no final-step helper text could ever render. */
  helpText?: string;
  /**
   * `files` ONLY (BRK-S145): how many documents this field takes. The effective
   * cap is the SMALLER of this and the served `rules.maxFilesPerField` — a
   * field asking for more than PMS accepts is a configuration mistake, and the
   * honest limit to enforce is the one the upload will actually survive.
   * Absent means the served per-field cap.
   */
  maxFiles?: number;
  /**
   * `files` ONLY: a narrower content-type/extension list than the served rules,
   * for a field that wants (say) a PDF and nothing else. It NARROWS and never
   * widens — `screenDocument` still applies the served rules, so a configuration
   * cannot talk the portal into offering a type PMS will refuse.
   */
  accept?: string[];
}

/**
 * The RESOLVED configuration driving one subproduct's flow. Step 1 and the final
 * step are code-owned surfaces; `eligibility` supplies step 1's attestations and
 * `steps` supplies everything between. An empty `steps` is legitimate (a
 * subproduct that needs nothing beyond step 1 and the terms).
 */
/**
 * Whether the spouses of every listed owner sign too (PMS-S341).
 *
 * A CLOSED enum rather than a boolean, mirroring the design's own reasoning: a
 * third value (community-property states only) is then a widening rather than a
 * reshape — the `broker_role` precedent.
 */
export type BondSpousesRule = "all" | "none";

/** The two numbers the owner half of the rule turns on. Plain numbers, never
 *  formatted strings: D-10 applies to a percentage as it does to currency. */
export interface BondIndemnityOwnersRule {
  /** Percent. An owner AT OR ABOVE this always signs — inclusive, Product's own
   *  word ("at least 10%"). */
  minimumShare: number;
  /**
   * Percent, or null. When set, further owners are added largest share first
   * until the listed owners hold MORE THAN this — exclusive, Product's own word
   * again. `null` is the no-top-up variant.
   */
  representedShare: number | null;
}

/**
 * Who has to sign the indemnity agreement, as the product configures it
 * (PMS-S341 § 1).
 *
 * TWO NUMBERS AND ONE SWITCH, and that is the whole of it. There is NO BROKER
 * CHOICE anywhere in this feature: discovery evaluates the rule against the cap
 * table and publishes the resulting signer list on the order, and this portal
 * renders it. Nothing here is a control.
 *
 * The comparisons are FIXED and not configurable — `minimumShare` inclusive,
 * `representedShare` exclusive — which is why neither appears on this object.
 */
export interface BondIndemnityRule {
  owners: BondIndemnityOwnersRule;
  spouses: BondSpousesRule;
}

export interface BondConfig {
  workflowTemplateId: string;
  eligibility: string[];
  steps: BondStep[];
  finalStep: BondFinalStep;
  /**
   * ADOPTED AHEAD OF THE PIN (BRK-S152, PMS-S393 / SUR-S159).
   *
   * OPTIONAL, and the absence is real: a configuration stored before the key
   * existed resolves to the house rule upstream, and a PMS that predates the
   * block sends nothing at all. Both read as "no rule stated here", which is
   * why the roster's copy prefers the ORDER's own `indemnity.rule` — the rule
   * the evaluation actually used — and falls back to this.
   */
  indemnity?: BondIndemnityRule;
  /**
   * The SHAPE of the flow this configuration drives (PMS-S434, BRK-S160).
   *
   * OPTIONAL, and the absence is `bond` — read through `bondFlowOf` and never
   * compared directly, because a configuration stored before the key existed
   * and a PMS that predates it must both behave exactly as they do today.
   *
   * A `preapproval` configuration declares an EMPTY `finalStep.fields`: the
   * wizard ends on the Indication chip, so there are no terms to collect. BRK
   * still reads `finalStep` the same way — the emptiness is the service's
   * statement, not a shape this client special-cases.
   */
  flow?: BondFlow;
}

/** GET /api/broker/bond/subproducts/{id}/config — the resolved config for a
 *  subproduct (product-level config inherited server-side). */
export function getResolvedBondConfig(
  request: AuthedRequester,
  subproductId: string,
): Promise<BondConfig> {
  return request<BondConfig>(
    `${BASE}/subproducts/${encodeURIComponent(subproductId)}/config`,
  );
}

// ─── Bond order (create / read) ───────────────────────────────────────────────

/** A bond order's lifecycle status. A pointer exists from draft start, so `draft`
 *  is the normal state while the broker is still filling the flow in.
 *
 *  `cancelled` (BRK-S181, PMS-S479) is a DISCARDED
 *  order: the broker chose "Discard and start over" on a pre-approval already
 *  in flight. It is kept, not deleted, is hidden from the order lists unless
 *  asked for, and refuses every further write with 409 `ORDER_CANCELLED`. The
 *  vocabulary is CLOSED on both sides, which is why the reader adopted the
 *  member before the first response carried it. */
export type BondSubmissionStatus = "draft" | "submitted" | "cancelled";

/** Which account the order is for: a brand-new insured, or one the brokerage
 *  already has on file. */
export type BondMode = "new" | "existing";

/** A candidate the server thinks might be the same insured as the one being
 *  entered. Brokerage-scoped and **advisory** — offered, never enforced. */
export interface BondDuplicateCandidate {
  accountId: string;
  legalName: string;
  accountNumber?: string | null;
  city?: string | null;
  state?: string | null;
}

/** The account fields a `new`-mode create carries (the insured being created). */
export interface BondAccountFields {
  legalName: string;
  state?: string;
  [key: string]: unknown;
}

export interface CreateBondSubmissionInput {
  subproductId: string;
  mode: BondMode;
  /** Required in `existing` mode — the account the order is filed against. */
  accountId?: string;
  /** Used in `new` mode to create the insured. */
  accountFields?: BondAccountFields;
  /** Set to proceed past an advisory duplicate warning ("Create new anyway"). */
  confirmDuplicates?: boolean;
  /**
   * What the broker attested to on the eligibility step (BRK-S170).
   *
   * SENT since the wave-1 re-pin (BRK-S179) — see
   * `BOND_CREATE_SENDS_ELIGIBILITY`, which is the decision, and the page, which
   * is where it is made. A block is only ever built when every statement has
   * been confirmed, so this is never a partial attestation.
   */
  eligibility?: BondEligibilityBlock;
}

/**
 * Whether this build puts the eligibility block on the create body
 * (BRK-S170).
 *
 * **A CONSTANT, not a feature flag.** It records one fact — does the PINNED PMS
 * contract accept the field — and `brokerBondsContractPin.test.ts` binds it to
 * the pinned spec, so it cannot be wrong for longer than a re-pin: the moment
 * the create's request body publishes `eligibility`, the guard fails until this
 * is flipped. It is not a runtime toggle and there is nothing to configure; the
 * SPA holds no contract at runtime (NN #5), so this is the only place the
 * question can be answered.
 *
 * Why BRK shipped the collection first at all: the per-item checkboxes are the
 * broker's experience of the step and they need nothing from the server. The
 * block is the only half that did, so it waited — and "absent ⇒ behave exactly
 * as today" was a claim with a test on it rather than an intention. The wave-1
 * re-pin published the field and the guard failed until this flipped, which is
 * the mechanism working rather than a decision anybody had to remember.
 */
export const BOND_CREATE_SENDS_ELIGIBILITY = true;

/**
 * The reserved key the final step's project-&-bond bag lives under. Code-owned
 * contract on BOTH sides (PMS `BROKER_FINAL_STEP_KEY`): the config names every
 * other step, this one is ours, so the server keys on the same literal.
 */
export const BOND_FINAL_STEP_KEY = "final";

/**
 * One owner on a bond order, as PMS's ROSTER names them (PMS-S244, adopting
 * discovery's contract 3.0.0).
 *
 * The roster exists so owner identity is a STATED FACT rather than an inference.
 * Before it, "which owner is this value about?" could only be answered from
 * arrival order, and arrival order is not identity — that is what put owner 2's
 * date of birth under owner 1 (SUR-S064 / BRK-S108).
 *
 * `ownerId` is PMS's own personnel id, the identity every repeated-block value is
 * keyed by upstream. There is deliberately **no vendor `elementId`** here: PMS
 * does not hand BRK the vendor's row id, nothing in this portal addresses an
 * owner by it, and reaching for one would be reaching past PMS.
 */
export interface BondOwner {
  ownerId: string;
  /** How to name this owner on screen. The ONLY per-owner label BRK has. */
  displayName: string;
  /**
   * DECLARED primacy. Read it and nothing else — never position, never "the one
   * that happens to have values". An owner who answered nothing still appears on
   * the roster, still marked, which is precisely the case order-based inference
   * got wrong.
   */
  isPrimary: boolean;
}

/**
 * The roster's primary owner, or **null** when nobody is marked.
 *
 * Deliberately `find(isPrimary)` with NO positional fallback. `owners[0]` would
 * re-introduce the exact inference the roster was published to retire, and it
 * would do it silently — a wrong name next to a date of birth reads as a fact.
 * When the roster is absent or marks nobody, the honest answer is "nobody said".
 */
export function primaryBondOwner(
  owners: readonly BondOwner[] | undefined,
): BondOwner | null {
  return owners?.find((owner) => owner.isPrimary) ?? null;
}

/**
 * How many owners answered a repeated path, keyed exactly as `values` is —
 * step key, then contract path (PMS-S238).
 *
 * This is the counterweight to the resolution above it. `values` publishes ONE
 * value for a repeated path, the primary owner's, which is what a flow that
 * captures one person needs; the cost is that a two-owner order renders
 * identically to a one-owner order, with nothing on screen saying so. The count
 * is what stops the record from quietly understating itself.
 *
 * A COUNT, deliberately not a resolved element id: an id would imply the reader
 * can resolve it to a person, and the only per-owner label BRK has is the
 * roster's `displayName`.
 */
export type BondElementCounts = Record<string, Record<string, number>>;

/**
 * How many owners answered fields on one step — the HIGHEST count among that
 * step's repeated paths — or **null** when the step is not about people at all.
 *
 * The highest, because paths legitimately differ: two owners may give a last
 * name where one gives an SSN, and the step was still about two people.
 *
 * Null for an absent step (the contract: "a path absent here does not repeat"),
 * for an absent bag, and for a bag carrying nothing positive — a malformed
 * count degrades to saying nothing rather than to "0 owners answered this"
 * (NN #7). A count of **1 is kept**: the contract includes it on purpose, so it
 * is an affirmation and not an inference from absence.
 */
export function ownersAnsweringStep(
  counts: BondElementCounts | undefined,
  stepKey: string,
): number | null {
  const forStep = counts?.[stepKey];
  if (!forStep) return null;
  let highest = 0;
  for (const count of Object.values(forStep)) {
    if (Number.isInteger(count) && count > highest) highest = count;
  }
  return highest > 0 ? highest : null;
}

/** A bond order as the flow reads it. `values` is the accumulated step bag, and
 *  `referred` is the sticky advisory flag a non-pass data pull sets. */
export interface BondSubmission {
  id: string;
  number: string;
  accountId: string;
  accountLegalName: string | null;
  subproductId: string;
  status: BondSubmissionStatus;
  referred: boolean;
  createdAt: string;
  /**
   * Which flow this order is running (PMS-S434, adopted in BRK-S160).
   *
   * REQUIRED upstream, OPTIONAL here for the consumer-first deploy window —
   * BRK ships ahead of PMS, and an order body without the key is an older PMS
   * rather than an order with no flow. `bondFlowOf` resolves both to `bond`,
   * which is what keeps an absent key byte-identical to today.
   *
   * It is the order's OWN copy rather than something derived from the
   * configuration: a filed pre-approval still reads "Complete" months later,
   * when the subproduct's configuration is nobody's business to re-resolve.
   */
  flow?: BondFlow;
  /**
   * Captured values, keyed by step key then contract path.
   *
   * EVERY `fields` step carries what THAT step captured — pushed and
   * bond-order-private values alike, in ONE bag, resolved server-side in
   * precedence order (discovery's per-step mirror, then the order's private
   * overrides, then the shared bag). A `data_pull` step carries no bag.
   *
   * A consumer does NOT redistribute anything itself (PMS-S238). BRK did until
   * BRK-S110: the two reads used to fill step keys differently — a create
   * seeded each step from the account prefill, while an order READ put only
   * bond-order-PRIVATE overrides under step keys and relayed every PUSHED value
   * in the shared bag alone (BRK-S107). `distributeBondValues` existed to paper
   * over that asymmetry and is GONE, because papering over it here was always a
   * second source of truth for where a value "should" have been.
   *
   * `final` is a RESERVED KEY carrying the WHOLE shared bag — the final step's
   * project-&-bond terms AND every value the broker pushed. It is NOT "the
   * final step's bag" and it is not narrowed to the paths no step owns, so a
   * pushed value appears TWICE: under its step and under `final`. Read the
   * final step's own fields out of it BY KEY; read every other step from its
   * own step key.
   *
   * Per-step `status` / `savedAt` are deliberately NOT published — they are
   * discovery's progress derivation for an underwriter, and a second answer to
   * "is this step done" would disagree with this flow's own form validity.
   *
   * A REPEATED path (`personal[].lastName`) is resolved upstream to the value
   * belonging to the owner `owners` marks primary, under EVERY key including
   * `final`. When that owner supplied nothing, the path is **legitimately
   * ABSENT** rather than resolved to a neighbour's value (PMS-S244). Absent is
   * UNANSWERED — not an error, and not a blank that implies the field is empty
   * for everyone.
   */
  values?: Record<string, Record<string, unknown>>;
  /**
   * WHO the owners on this order are, and which one is primary (PMS-S244).
   *
   * OPTIONAL, and absence has an exact meaning: **nobody declared a roster**.
   * It does NOT mean "this order has no owners", and it is never a cue to fall
   * back to ordering — an absent roster is less information than a roster, not
   * permission to guess. `primaryBondOwner` returns null for it and every
   * surface says "not stated" rather than naming someone.
   */
  owners?: BondOwner[];
  /**
   * How many owners answered each repeated path (PMS-S238) — see
   * `ownersAnsweringStep`, which is the only thing that reads it.
   *
   * OPTIONAL, and absence means the server published no counts, never "one
   * owner". Present for every repeated path in `values`, count 1 INCLUDED.
   */
  elementCounts?: BondElementCounts;
  /** Settled data-pull outcomes, keyed by step key (present on reload so the
   *  flow can restore a pull it already ran). */
  stepRuns?: Record<string, BondStepRun>;
  /**
   * The broker's answer to the Signal Advance offer on THIS order (PMS-S435,
   * adopted in BRK-S161). ALWAYS PRESENT upstream.
   *
   * It decides whether the offer is shown and whether the connect-bank /
   * connect-accounting links appear — the enrolment is what makes discovery
   * mint them. It does NOT say which program is in force: that is
   * `preapproval.programType`, and inferring one from the other would tell a
   * broker they are on Advance because they asked to be.
   *
   * OPTIONAL here for the deploy window only; an absent key reads as `null` —
   * nobody has answered — which is the state that offers the program.
   */
  advanceOptIn?: BondAdvanceOptIn | null;
  /**
   * The REPEAT request's declaration, as the order carries it back (BRK-S162,
   * consuming PMS-S462).
   *
   * ⚠️ READ AHEAD OF THE PIN. It is what makes a RESUMED repeat request show
   * the answer it was given rather than an empty radio group — the server is
   * the state (NN #9), and without the echo the declaration would live only in
   * a React `useState` that a reload throws away. Optional and nullable, so an
   * order from a PMS that predates PMS-S462 simply asks the question again.
   *
   * `null` is a real value: an order on the repeat spine that has not answered
   * yet — and it is the contract's own spelling, since the wave-1 re-pin marks
   * the key REQUIRED and nullable. It stays OPTIONAL at the reader for the
   * deploy window only: an order from a PMS that predates the key must still
   * parse, and an absent key reads the same as `null` at the one place that
   * consumes it.
   */
  changeDeclaration?: ChangeDeclaration | null;
  /**
   * The link the broker hands to their customer, who uses it to connect their
   * accounting system (PMS-S256 / SUR-S088).
   *
   * OPTIONAL, and the absence is a FACT rather than a gap: the key is ABSENT —
   * never null — when the order's configuration declared no post-submission
   * accounting step. A bond product that asks for no financials is a normal
   * outcome, not an underwriting flag, and there is nothing for the broker to
   * do about it. So the panel renders nothing at all rather than an empty card.
   *
   * Published on EVERY bond-order response, the submit's included, which is why
   * the confirmation screen needs no refetch to show it.
   *
   * THE LINK AND NOTHING ELSE. The financial data fetched behind it is
   * underwriter-only (SUR-S052 answer 1). The order's own config declaration
   * (`postSubmission.accountingLink`) carries metric names and is deliberately
   * NOT part of `BondConfig` here: BRK does not read it, so it cannot render
   * it. Adding it needs a product decision, not a refactor.
   */
  accountingLink?: BondAccountingLink;
  /**
   * The OTHER link the broker hands to their customer, who uses it to connect
   * their BANK through Plaid (PMS-S259 / SUR-S100, contract 4.15.0).
   *
   * OPTIONAL, and — unlike `accountingLink` — the KEY has only ONE kind of
   * absence. A draft, an order with no owner on file, a workflow with no
   * consent step and a vendor outage are one fact to a broker: there is nothing
   * to hand over yet. So the key is simply ABSENT, never null.
   *
   * An order filed BEFORE 4.15.0 shipped has none and never will, which is the
   * same absence and renders the same way: nothing at all.
   *
   * NO CONFIGURATION GATES IT. `accountingLink` appears only when the order's
   * config declared an accounting step; this one is minted for every submitted
   * order. The two are independent, which is why they are two cards.
   *
   * Connection state ARRIVED as an added key on this object (SUR-S118 /
   * PMS-S270, adopted in BRK-S121) — exactly what the one-key object was shaped
   * for. It is a second, INDEPENDENT optional: see `BondBankLink.state`.
   *
   * THE LINK AND NOTHING ELSE, the same line `accountingLink` holds above and
   * for the same reason: the financial data reached through it is
   * underwriter-only (SUR-S052 answer 1).
   */
  bankLink?: BondBankLink;
  /**
   * Where this order stands with Signal's PROGRAM PREAPPROVAL (PMS-S306,
   * adopted in BRK-S132).
   *
   * TWO ABSENCES, and they are different facts — both render nothing, which is
   * why keeping them apart costs nothing and why neither is defaulted:
   *   • the KEY IS ABSENT for a PMS that predates PMS-S306. Nothing has been
   *     said, because nothing can be. This is the consumer-first window (BRK
   *     deploys ahead of PMS), and it closes on its own.
   *   • `null` is PMS SAYING there is nothing to say: the order's configuration
   *     declares no `preapproval` step, so no preapproval was ever evaluated.
   *     A bond product that does not prequalify is a normal product outcome.
   *
   * NOTHING ON THIS OBJECT EXPLAINS A DECISION, on purpose (Aaron 2026-09-02).
   * There is no score, no reason, no surety name, no A.M. Best rating and no
   * Treasury status — not "not read here", but not published, because Signal
   * cannot state them to a broker. `test/lib/preapprovalDiscretion.test.ts` is
   * what keeps that true of this repo as a whole rather than of this comment.
   */
  preapproval?: BondPreapproval | null;
  /**
   * How the bid came out, once the broker has said (PMS-S316).
   *
   * REQUIRED and NULLABLE upstream; OPTIONAL here only for the deploy window.
   * `null` is how "nobody has told us yet" is spelled — there is deliberately
   * no `pending` outcome — and an absent key is a PMS that predates the block.
   * Both are one fact to this portal (nothing to render), so unlike
   * `preapproval` they are not kept apart.
   *
   * OMITTED FROM THE LIST ROW upstream, for the reason `preapproval` is: a
   * `null` on a summary would read as a statement only the detail read can
   * make.
   */
  bidResult?: BondBidResult | null;
  /** The signed contract uploaded for THIS order, resolved by provenance —
   *  required and nullable upstream, optional here for the same deploy window.
   *  See `BondFinalContract`: a POINTER, downloaded through the ACCOUNT. */
  finalContract?: BondFinalContract | null;
  /**
   * WHO HAS TO SIGN the indemnity agreement on this order (PMS-S341, adopted in
   * BRK-S152).
   *
   * TWO ABSENCES, kept apart the way `preapproval`'s are and for the same
   * reason — both render nothing, so keeping them apart costs nothing:
   *   • the KEY IS ABSENT for a PMS that predates the block. This is the
   *     consumer-first deploy window (PMS → BRK → SUR) and it closes on its own.
   *   • `null` is PMS SAYING there is nothing to say: the order's configuration
   *     declares no indemnitor roster step, so no signer list was ever
   *     evaluated. A bond product that asks for no personal indemnity is a
   *     normal product outcome.
   *
   * COMPUTED UPSTREAM, ALWAYS. Nothing in this portal decides who signs — the
   * evaluation is deterministic, it belongs to discovery, and a client that
   * re-derived it would be a second answer to a question with one.
   */
  indemnity?: BondIndemnity | null;
  /**
   * Where the order's GENERAL INDEMNITY AGREEMENT stands (PMS-S483, adopted
   * in BRK-S182).
   *
   * REQUIRED and NULLABLE upstream since the re-pin onto PMS 1726b1c; OPTIONAL
   * here so a missing key costs a section rather than the order read. `null`
   * is PMS saying the order is not approved (a draft, a referred order, a
   * pre-approval not approved); `showsGiaSection` treats an absent key alike. The signers themselves are a read of their own
   * (`getBondGia`) — the order carries the status only.
   */
  gia?: BondGiaSummary | null;
}

// ─── Who has to sign (PMS-S341 / PMS-S393, adopted in BRK-S152) ──────────────

/**
 * Where the order stands against its indemnity rule.
 *
 *   • `complete`          — every seat the rule names is filled.
 *   • `incomplete`        — a named signer is not on the roster yet.
 *   • `needs_information` — the rule cannot be finished from what is on file:
 *     an owner's marital status is unknown, or the declared shares never reach
 *     the represented line. The fix is a fact, not a person.
 */
export type BondIndemnityStatus =
  | "complete"
  | "incomplete"
  | "needs_information";

/** Why a seat exists. `spouse_of`'s `subject` is the OWNER the seat derives
 *  from — never the spouse — which is what lets the checklist say whose. */
export type BondSignerBasis = "owner" | "spouse_of";

/** Where one seat stands. `listed` is "this person is on the roster", NOT "they
 *  have signed": nothing in this flow collects a signature. */
export type BondSignerStatus = "listed" | "missing" | "needs_information";

/**
 * The person a seat is about, as the OWNER ROSTER names them.
 *
 * `ownerId` — the roster's identity (contract 3.0.0 / SUR-S065), which is PMS's
 * own `personnel.id` for a person it already holds. NEVER a position: the whole
 * reason the roster exists is that an index is not an identity, and an owner
 * who answered nothing has no element at all. The checklist links to the block
 * that fixes a seat by this id.
 */
export interface BondSignerSubject {
  ownerId: string;
  name: string | null;
  ownershipPercentage: number | null;
}

export interface BondSigner {
  basis: BondSignerBasis;
  subject: BondSignerSubject;
  /**
   * The SPOUSE's own roster identity.
   *
   * REQUIRED AND NULLABLE — published on every seat (surety contract 8.1.0 /
   * SUR-S159), never omitted. `null` on an `owner` seat, which has no spouse
   * row by definition, and on a spouse seat whose person is not on the roster
   * yet, which is exactly what `missing` means. Two facts, one spelling,
   * because a reader does nothing different with them: there is nobody to point
   * at.
   *
   * Typed required rather than optional deliberately: an optional member would
   * let a producer that stopped sending it read as a seat with no person, which
   * is a claim rather than an absence.
   */
  spouseOwnerId: string | null;
  status: BondSignerStatus;
}

export interface BondIndemnity {
  status: BondIndemnityStatus;
  signers: BondSigner[];
  /** The rule this evaluation used — the plain facts a broker may see. Read in
   *  preference to the configuration's copy: this is the one that ran. */
  rule: BondIndemnityRule;
  /** Combined share of the listed owners, against `rule.owners.representedShare`. */
  representedShareListed: number;
}

const RENDERED_INDEMNITY_STATUSES = [
  "complete",
  "incomplete",
  "needs_information",
] as const;
const RENDERED_SIGNER_BASES = ["owner", "spouse_of"] as const;
const RENDERED_SIGNER_STATUSES = [
  "listed",
  "missing",
  "needs_information",
] as const;

function known<T extends string>(
  vocabulary: readonly T[],
  tag: unknown,
): T | null {
  if (typeof tag !== "string") return null;
  return vocabulary.find((member) => member === tag) ?? null;
}

/**
 * Whether the whole block is one this build can render honestly.
 *
 * THREE CLOSED VOCABULARIES, and every one of them is checked, because the cost
 * of a member arriving unrecognised is not a blank field — it is a checklist
 * that renders the seats it understands, silently drops the one it does not,
 * and puts a live Continue in front of a roster that looks finished. That is
 * BRK-S114's class, and the answer is the same: name it, and render the guarded
 * card instead.
 *
 * A block with NO signers is supported: "this rule names nobody" is an answer.
 */
export function isIndemnitySupported(
  indemnity: BondIndemnity | null | undefined,
): boolean {
  if (!indemnity) return true;
  if (known(RENDERED_INDEMNITY_STATUSES, indemnity.status) === null) {
    return false;
  }
  if (!Array.isArray(indemnity.signers)) return false;
  return indemnity.signers.every(
    (signer) =>
      known(RENDERED_SIGNER_BASES, signer?.basis) !== null &&
      known(RENDERED_SIGNER_STATUSES, signer?.status) !== null,
  );
}

/**
 * Whether the roster step is DONE, and the only rule that decides it.
 *
 * The server's own answer and nothing else (BRK-S122): there is no optimistic
 * success state here, because "everybody the rule names is on the roster" is a
 * judgment made from the cap table upstream, and a client that guessed it would
 * tick a step whose signer list the underwriter is about to find short.
 *
 * It does NOT block: an incomplete roster can still be saved and the order can
 * still be submitted — it is referred, which is the existing mechanism and the
 * one the copy names.
 */
export function isIndemnityComplete(
  indemnity: BondIndemnity | null | undefined,
): boolean {
  return indemnity?.status === "complete";
}

/**
 * Whether a program preapproval has been decided.
 *
 * `pending` is the ONLY unsettled member, and it is not only the pre-run state:
 * PMS mirrors the decision onto the order after the run settles, so a read that
 * lands in that gap answers `pending` for an order whose checks are done. That
 * lag is the reason the Indication chip polls rather than trusting one read.
 */
export type BondPreapprovalStatus = "approved" | "not_granted" | "pending";

/**
 * WHICH evaluation this order is reading.
 *
 *   • `submission` — this order's own run produced it.
 *   • `account`    — the insured already held one. The order ran nothing, which
 *     is why BRK-S134 omits the two chips entirely rather than showing them
 *     done: a chip marked ✓ for work that never happened is a claim about this
 *     order that nobody made.
 */
export type BondPreapprovalSource = "account" | "submission";

/**
 * The program terms a granted preapproval carries — and the whole of them.
 *
 * `ratePer1000` is dollars of premium per $1,000 of bond, which is the surety
 * market's own unit and the one PMS stores. It is NOT a percentage: rendering
 * it as one directly would overstate the rate by 10×. `bondRatePercent` is the
 * single conversion, and it is a DISPLAY conversion — the wire keeps the rate
 * per $1,000 (D-10's display/wire split, which runs both ways).
 */
export interface BondPreapprovalTerms {
  singleBondLimit: number;
  aggregateProgramLimit: number;
  ratePer1000: number;
}

/** The order view's preapproval block. `terms` and `evaluatedAt` are null until
 *  there is a decision to carry, and stay null for `not_granted` — there are no
 *  terms on an ungranted program. */
/**
 * WHICH standing surety program is in force (PMS-S435, adopted in BRK-S161).
 *
 * Signal runs two, and until now this portal had no word for the difference.
 * **Signal Express** is granted off the bureau pulls alone; **Signal Advance**
 * is the program the insured's connected bank and accounting feeds support.
 *
 * ⚠️ `advance`, NEVER `advanced`. The programme is Signal Advance; the earlier
 * drafts of these stories said "Advanced" and the word is corrected wherever it
 * appears (`test/lib/signalAdvanceNaming.test.ts` is what keeps that true of the
 * repo rather than of this comment).
 *
 * BROKER-SAFE, and the line is worth naming: a program NAME is what Signal
 * OFFERS. The rater that chose it stays underwriter-only, which is why PMS
 * derives this from the rater key on its own side and publishes only the
 * answer.
 */
export type BondProgramType = "express" | "advance";

/**
 * The broker's answer to the Signal Advance offer on ONE order (PMS-S435).
 *
 * FOUR STATES INCLUDING `null`, and the two absences are different facts:
 * `null` is a conversation that has not happened, `declined` is one that did.
 * Offering the program again to somebody who declined is fine; treating them as
 * never having been asked is not.
 */
export type BondAdvanceOptIn = "this_project" | "future_projects" | "declined";

export interface BondPreapproval {
  status: BondPreapprovalStatus;
  source: BondPreapprovalSource;
  /**
   * Which program is in force — `null` whenever none is (PMS-S435).
   *
   * Null on `pending` and on `not_granted`, and never defaulted: naming a
   * program type beside a status that says the insured holds nothing would name
   * a program they do not have.
   */
  programType: BondProgramType | null;
  terms: BondPreapprovalTerms | null;
  evaluatedAt: string | null;
  /**
   * Whether the Program Indication LETTER can be fetched yet.
   *
   * Its own axis, and not derivable from `status`: rendering the letter is a
   * later step than deciding the program, so an `approved` order can carry no
   * PDF for a moment. Reading `status === "approved"` as "there is a letter" is
   * how a broker gets an empty frame; the view renders a "preparing your
   * letter" state that re-checks instead.
   */
  indicationAvailable: boolean;
  /**
   * When the standing program runs out — one year from the decision, DERIVED
   * upstream rather than stored (PMS-S4xx, adopted at the wave-1 re-pin).
   *
   * **Null whenever no program is in force, a LAPSED one included.** An expired
   * program reads exactly like a refusal on this surface — the same rule a
   * withdrawal follows — so there is never a past date here and never a way to
   * tell the two apart. A non-null value is always in the future, which is what
   * makes it safe to render without comparing it to a clock.
   */
  expiresAt: string | null;
}

/**
 * Has the preapproval been decided? FALSE for an absent block as well as for
 * `pending`: a PMS that says nothing has not said no.
 */
export function isPreapprovalSettled(
  preapproval: BondPreapproval | null | undefined,
): boolean {
  return preapproval != null && preapproval.status !== "pending";
}

/**
 * Does the INSURED already hold a granted preapproval (BRK-S134)?
 *
 * Both halves are required, and the `status` half is the one worth naming: a
 * standing `not_granted` is treated exactly like a fresh order, so the checks
 * run again. A prior no is not a permanent no — the insured's figures move, and
 * a wizard that skipped straight past on the strength of one would make a
 * retry impossible from the portal.
 */
export function hasStandingPreapproval(
  preapproval: BondPreapproval | null | undefined,
): boolean {
  return preapproval?.source === "account" && preapproval.status === "approved";
}

/**
 * The bond rate as a PERCENT string, to two decimals — `25` → `"2.50"`.
 *
 * Rate per $1,000 ÷ 10 is rate per $100, which is what a percent is. One
 * function because the same three figures appear on the indication view and
 * again on BRK-S134's standing banner, and a second copy of `/10` is how the
 * two disagree.
 */
export function bondRatePercent(ratePer1000: number): string {
  return (ratePer1000 / 10).toFixed(2);
}

/**
 * Re-read the order until its preapproval SETTLES, resolving with the settled
 * block.
 *
 * Why a second poller beside `pollBondStepRun`, rather than trusting the read
 * that follows a settled run: the decision is MIRRORED onto the order after the
 * run settles, so the first read can legitimately still say `pending`. The
 * honest options at that moment are a bounded wait or a blank panel, and the
 * flow takes the wait.
 *
 * Rejects with a handled `ApiError` on timeout, exactly as the run poller does,
 * so the Indication chip renders one recovery state for "it timed out" and "the
 * read failed" alike (NN #7) — and, as everywhere in this flow, a preapproval
 * never blocks the order.
 *
 * A read that reports the key ABSENT or `null` resolves immediately with null:
 * there is nothing to wait for, and waiting on a PMS that publishes no
 * preapproval would spin until the deadline every time.
 */
export async function pollBondPreapproval(
  request: AuthedRequester,
  id: string,
  // `PollOptions` minus its run-shaped `onUpdate`: the same knobs, reporting
  // the thing THIS poller reads.
  options: Omit<PollOptions, "onUpdate"> & {
    onUpdate?: (preapproval: BondPreapproval | null) => void;
  } = {},
): Promise<BondPreapproval | null> {
  const {
    intervalMs = 2000,
    timeoutMs = 30_000,
    onUpdate,
    wait = sleep,
  } = options;
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const order = await getBondSubmission(request, id);
    const preapproval = order.preapproval ?? null;
    onUpdate?.(preapproval);
    // Nothing published, or nothing to publish — either way there is no
    // decision coming that another read would reveal.
    if (preapproval === null || isPreapprovalSettled(preapproval)) {
      return preapproval;
    }
    if (Date.now() >= deadline) {
      throw new ApiError(
        504,
        "Your indication is taking longer than expected to finish. You can keep going and we’ll follow up.",
      );
    }
    await wait(intervalMs);
  }
}

/**
 * Where an order's accounting connection has got to. Its OWN AXIS, like
 * `referred` — never a value on `status`, and a submitted order stays
 * `submitted` in every one of them.
 *
 * The three that arrive TODAY:
 *
 *   • `unminted` — minting has not succeeded. It is BEST EFFORT upstream and
 *     never fails a submit, so the order is still submitted and still numbered
 *     with nothing to hand over yet. Not an error, and not a state the broker
 *     can act on.
 *   • `pending`  — the link is live and waiting on the customer.
 *   • `linked`   — they connected.
 *
 * And the four that arrive once discovery deploys (BRK-S119):
 *
 *   • `syncing`     — connected, and data is still coming across.
 *   • `connected`   — connected. The new vocabulary's spelling for the fact
 *     `linked` already carries, which is why BOTH are members: `linked` is what
 *     is on the wire today, and dropping it to "clean up" the union is how a
 *     live order stops reading the day this deploys.
 *   • `needsReauth` — the connection lapsed and the customer has to reconnect.
 *   • `disabled`    — the connection was switched off.
 *
 * ADOPTED AHEAD OF THE PIN, DELIBERATELY. BRK mirrors PMS with a CLOSED reader,
 * so a widening arrives as a BREAK rather than an addition — the reader has to
 * already know a member on the FIRST response that carries it. That inverts the
 * usual order: BRK → PMS → discovery, consumers FIRST, and BRK lives briefly
 * with a union wider than anything being sent. Nothing here may key behaviour
 * off the four new members before that window closes.
 *
 * WHAT THEY MEAN PRECISELY IS UPSTREAM'S TO SAY, and the exact wording a broker
 * reads for each is the rendering half of BRK-S119, not this one. This union is
 * the vocabulary and nothing else: see `AccountingLinkPanel`, which still asks
 * only whether the customer has connected and whether there is a url to hand
 * over, and see `docs/bonds.md` for the copy each state is headed for.
 */
export type BondAccountingLinkState =
  | "unminted"
  | "pending"
  | "linked"
  | "syncing"
  | "connected"
  | "needsReauth"
  | "disabled";

/** The three KEYS the contract publishes, and no fourth — the widening in
 *  BRK-S119 is to `state`'s vocabulary, not to this shape. Both ids are null in
 *  `unminted`, which is why nothing here may be rendered without checking. */
export interface BondAccountingLink {
  connectionId: string | null;
  linkUrl: string | null;
  state: BondAccountingLinkState;
}

/**
 * The state to RENDER, with the legacy spelling folded onto the new vocabulary:
 * `linked` reads as `syncing` and everything else reads as itself.
 *
 * THE ONE PLACE THE FOLD LIVES (BRK-S119's rendering half). `linked` and
 * `connected` are the same fact in two vocabularies, and the fold has to be
 * `syncing` rather than `connected`: a `linked` order is one discovery has not
 * spoken about since the widening, so claiming its sync has FINISHED would be a
 * claim nothing made. `syncing` says "connected, data still coming" and keeps
 * the panel refreshing until the new vocabulary confirms which terminal state it
 * is really in — which is exactly what a legacy order needs.
 *
 * Every reader goes through here, so the day discovery stops emitting `linked`
 * NOTHING changes: the member simply stops arriving. That is the difference
 * between one fold and a `state === "linked" || state === "connected"` growing a
 * third spelling in every branch that asks.
 */
export function canonicalAccountingLinkState(
  state: BondAccountingLinkState,
): BondAccountingLinkState {
  return state === "linked" ? "syncing" : state;
}

/**
 * Has the customer connected their accounting system?
 *
 * The successor to `AccountingLinkPanel`'s `state === "linked"` boolean, which
 * would have gone permanently false the moment discovery replaced `linked` with
 * `syncing`/`connected` — a connected customer's broker reading "waiting for
 * your customer to connect" forever, with nothing on screen saying otherwise.
 *
 * TRUE for all three connected spellings (`linked`, `syncing`, `connected`) and
 * for nothing else. `needsReauth` is deliberately FALSE: the connection lapsed,
 * so the customer has to act again, and a surface that reads it as connected is
 * the same silence one story later.
 */
export function isAccountingLinkConnected(
  state: BondAccountingLinkState,
): boolean {
  return state === "linked" || state === "syncing" || state === "connected";
}

/**
 * Is this state SETTLED — will nothing move without somebody acting?
 *
 * The poller's stop condition, and the only thing that decides it (BRK-S119).
 *
 *   • NON-TERMINAL: `unminted` (the mint has not landed), `pending` (waiting on
 *     the customer), `linked`/`syncing` (data still coming across). All four
 *     change on their own, which is what makes refreshing worth doing.
 *   • TERMINAL: `connected` (done), `needsReauth` and `disabled` (waiting on a
 *     person — the customer or support — not on a clock).
 *
 * `needsReauth` counts as settled on purpose. The customer redoing the link is
 * not something a poll observes on a 12-second cadence, and a panel that keeps
 * reading a lapsed connection forever is a request loop with no reader.
 */
export function isAccountingLinkSettled(
  state: BondAccountingLinkState,
): boolean {
  const canonical = canonicalAccountingLinkState(state);
  return (
    canonical === "connected" ||
    canonical === "needsReauth" ||
    canonical === "disabled"
  );
}

/**
 * How far the customer has got with the BANK link (contract 5.5.0 / SUR-S118,
 * relayed by PMS-S270, adopted here in BRK-S121).
 *
 * ITS OWN AXIS, and NOT the accounting link's. Four members where that has
 * seven, and the gap is the VENDOR rather than a simplification we chose:
 * Plaid-via-Lendflow publishes no connection health at all — no `needs_update`,
 * no `is_disabled`, nothing that says a token went stale — so `needsReauth` and
 * `disabled` have no evidence behind them here and are not invented. A state
 * nothing can ever derive is worse than one that is missing: the first is a lie
 * a consumer writes copy against.
 *
 *   • `unminted`     — no link was minted. PUBLISHED ON THE AXIS AND NEVER SENT
 *     ON THIS READ: the absence of the whole `bankLink` key is what says this,
 *     and discovery's serializer cannot produce a key with `unminted` in it. It
 *     is a member anyway, because a closed mirror must accept everything the
 *     contract PUBLISHES rather than everything a comment says will arrive —
 *     the cheapest possible way to avoid a failed read if it ever is sent.
 *   • `pending`      — a link is live and nobody has seen the customer finish
 *     Plaid. Waiting on the CUSTOMER, and the state most orders are in. It is
 *     also, honestly, what upstream publishes when no read has happened yet: an
 *     unobserved connection and an unfinished one are indistinguishable from
 *     stored evidence, and this is the earlier of the two.
 *   • `connected`    — they completed Plaid. NOT the same as having figures.
 *   • `figuresReady` — bank accounts have actually landed.
 *
 * `connected` AND `figuresReady` ARE TWO STATES ON PURPOSE. A completed link
 * fetches nothing by itself — the fetch is a separate Plaid orchestration — so
 * a customer who connected twenty minutes ago can still have zero accounts
 * (measured on a real deal, SUR-S109). Collapsing them into one "connected"
 * would promise figures that are not there, and it is why `connected` is the
 * state this card most needs to keep refreshing out of.
 */
export type BondBankLinkState =
  | "unminted"
  | "pending"
  | "connected"
  | "figuresReady";

/**
 * The bank link: a url, and how far the customer has got with it (PMS-S259,
 * given a `state` by PMS-S270).
 *
 * `linkUrl` is NOT nullable, which is the standing asymmetry with
 * `BondAccountingLink` above: there is no minted-but-empty state, so no url
 * means no key rather than a key with a null in it. Normally an
 * `https://sgn.al/<slug>` short link that redirects to Lendflow — shortening is
 * best effort, so the same field carries Lendflow's raw url when it fails.
 * Render whatever is here and do not inspect which kind it is.
 */
export interface BondBankLink {
  linkUrl: string;
  /**
   * OPTIONAL, and the optionality is load-bearing rather than defensive.
   *
   * ⚠️ AN ABSENT `state` MEANS AN OLDER DISCOVERY, NOT A STATE. Every order
   * minted before contract 5.5.0 carries a url and no state, and those orders
   * are NOT `pending` — nothing is known about them. So this is never
   * defaulted and never nullable: absence has one spelling here and it is the
   * key being gone. Defaulting it to `pending` would publish a claim about a
   * customer's progress that nobody made, for every order filed before the key
   * existed.
   *
   * Two absences on one object, and they are different facts: no `bankLink` at
   * all means there is nothing to hand over (no card); a `bankLink` with no
   * `state` means the link is real and its progress is unknown (the card, and
   * no status line).
   */
  state?: BondBankLinkState;
}

/**
 * Has the customer connected their bank?
 *
 * TRUE for `connected` AND `figuresReady`, which is the trap worth naming:
 * `figuresReady` is a LATER fact than `connected`, not a different one, so a
 * check spelled `state === "connected"` would turn the indicator back off at
 * the moment the connection finally became useful to an underwriter.
 */
export function isBankLinkConnected(state: BondBankLinkState): boolean {
  return state === "connected" || state === "figuresReady";
}

/**
 * Is this state SETTLED — will nothing move without somebody acting?
 *
 * ONLY `figuresReady`, and the list is shorter than the accounting link's for
 * the same vendor reason the vocabulary is: there is no `needsReauth` and no
 * `disabled` on this axis to wait on a person.
 *
 * `connected` IS NOT SETTLED. The figures fetch is a separate orchestration
 * upstream, so a card that stopped refreshing there would tell a broker their
 * customer's figures were still coming across — forever, on an order whose
 * figures had already landed.
 */
export function isBankLinkSettled(state: BondBankLinkState): boolean {
  return state === "figuresReady";
}

// ─── The bid result (BRK-S141, consuming PMS-S317 / SUR-S152) ────────────────

/** How the bid came out. Two members, and the contract publishes no third —
 *  "pending" is the absence of the whole block. */
export type BondBidOutcome = "won" | "lost";

/**
 * The broker's own report on their own bid.
 *
 * ABSENT or **null** when nobody has reported yet, and the two are one fact to
 * this portal: `null` is the contract's "no report" and the key being absent is
 * the deploy window before PMS publishes it at all. Both render the empty
 * panel, so unlike `preapproval` there is nothing gained by keeping them apart.
 */
export interface BondBidResult {
  outcome: BondBidOutcome;
  /** NULL on a win, and on a loss the broker could not find a figure for.
   *  Never 0 — a zero next-lowest bid is a claim, and a false one. */
  nextLowestBidAmount: number | null;
  /** Required upstream only for the >10%-below case; null otherwise. Accepted
   *  on EITHER outcome — a note about a bid they won is theirs to make. */
  explanation: string | null;
  /**
   * OUR bid — the figure the 10% rule is measured against.
   *
   * REQUIRED and NULLABLE (as-built). Read from the order's own submitted bag
   * (`estimatedContractPrice`, then `bondAmount`) and SNAPSHOTTED by discovery,
   * "so the comparison stays explainable afterwards" — which is why the panel
   * prefers it over re-reading the bag: a later edit to the order cannot
   * silently restate what the rule was measured against.
   *
   * `null` when the order carried no readable bid of ours, in which case the
   * rule is simply NOT EVALUATED — a gap in our data must not refuse the
   * broker's answer. Published "so a consumer can EXPLAIN a refusal, never so
   * it can enforce one".
   */
  ourBidAmount: number | null;
  recordedAt: string;
}

/**
 * The signed contract uploaded for THIS order — a POINTER, not a download.
 *
 * Resolved by PROVENANCE rather than by "the account's latest signed contract",
 * so an insured with two won bids keeps two contracts straight. The bytes come
 * from the ACCOUNT's document route
 * (`downloadAccountDocument`, own-scoped through the account, PMS-S315) —
 * there is deliberately no second download route on the order, which is why
 * this panel needs the account id it already has.
 *
 * Always the LATEST version: a re-signed or corrected contract APPENDS rather
 * than replacing.
 */
export interface BondFinalContract {
  documentId: string;
  filename: string;
  uploadedAt: string;
}

/** The write body. `nextLowestBidAmount` is STRICTLY POSITIVE upstream, and
 *  both fields are optional HERE and branch-required THERE, deliberately: the
 *  server decides which of its three refusals applies, and a client that
 *  pre-refused the body would answer for it. */
export interface BidResultInput {
  outcome: BondBidOutcome;
  nextLowestBidAmount?: number;
  /** `maxLength: 2000` upstream. */
  explanation?: string;
}

/**
 * PUT /api/broker/bond/submissions/{id}/bid-result — record (or CORRECT) how
 * the bid came out.
 *
 * A second write REPLACES the first, because the flow is a correction: a broker
 * who reported a loss and then learned they won must be able to say so, and an
 * append-only record of guesses is not what an underwriter needs here.
 *
 * Answers 409 on a DRAFT (`NOT_SUBMITTED`) and 422 for one of three refusals —
 * see `bidResultRefusalField`, which is what puts each under the right input.
 */
export function setBondBidResult(
  request: AuthedRequester,
  id: string,
  input: BidResultInput,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${BASE}/submissions/${encodeURIComponent(id)}/bid-result`,
    { method: "PUT", body: input },
  );
}

/**
 * WHAT the bid-result write can refuse, and why the panel needs no table for it
 * (PMS-S317's reconciliation against SUR-S152).
 *
 * THREE refusals, not one, and two are easy to mistake for a 200:
 *   • `BID_EXPLANATION_REQUIRED` — the gap rule. Carries `details.gapPercent`
 *     AS A PERCENTAGE (12.5, not 0.125), and the sentence already states it.
 *   • `BID_AMOUNT_REQUIRED` — a lost bid with no next-lowest figure. The
 *     record's whole point is missing: it is what the rule is computed from.
 *   • `BID_AMOUNT_NOT_APPLICABLE` — a next-lowest figure on a WON result.
 *     REFUSED rather than ignored, because ignoring it would discard a number
 *     the broker typed while answering 200.
 *
 * ⚠️ THE ENVELOPE IS `details.field`, SINGULAR — not the `details.fields` ARRAY
 * every other structured refusal in this flow uses. Upstream's own note is that
 * its first draft sent the array (copying the configuration editor's shape) and
 * "BRK would have read `undefined` for the field to attach the error to".
 * `anchorServerError` reads both spellings for exactly that reason, which is
 * why there is no per-code mapping here: the envelope names the field and
 * carries the sentence, so nothing in this portal has to branch on the code.
 *
 * A DRAFT answers 409 (`NOT_SUBMITTED`) — the panel is only offered on a filed
 * order, so that one is a guard rather than a state.
 */

/**
 * The outcome of a create. PMS may answer `200` with duplicate candidates
 * INSTEAD of creating — that is a typed advisory, **not** an error, so it is
 * modeled as a discriminated union rather than thrown: the caller offers the
 * candidates and can retry with `confirmDuplicates: true`.
 */
export type CreateBondSubmissionResult =
  | { kind: "created"; submission: BondSubmission }
  | { kind: "duplicates"; candidates: BondDuplicateCandidate[] };

function hasDuplicates(
  body: unknown,
): body is { duplicates: BondDuplicateCandidate[] } {
  if (typeof body !== "object" || body === null) return false;
  const list = (body as Record<string, unknown>).duplicates;
  return Array.isArray(list) && list.length > 0;
}

/** A created order must at least identify itself. Checked because the two 200
 *  shapes are told apart by a FIELD NAME: a response that is neither (a renamed
 *  `duplicates`, say) would otherwise sail through as an order with no id and no
 *  number, and the flow would carry on against nothing. */
function isOrder(body: unknown): body is BondSubmission {
  if (typeof body !== "object" || body === null) return false;
  const b = body as Record<string, unknown>;
  return typeof b.id === "string" && typeof b.number === "string";
}

/**
 * POST /api/broker/bond/submissions — start a bond order (own-scoped; PMS forces
 * the brokerage from the session and mints the SB number).
 *
 * Returns `{kind: "duplicates"}` when the server offers candidates instead of
 * creating. An EMPTY `duplicates` array is not an advisory — nothing to offer —
 * so it is only treated as one when there is at least one candidate.
 */
export async function createBondSubmission(
  request: AuthedRequester,
  input: CreateBondSubmissionInput,
): Promise<CreateBondSubmissionResult> {
  const body = await request<unknown>(`${BASE}/submissions`, {
    method: "POST",
    body: input,
  });
  if (hasDuplicates(body)) {
    return { kind: "duplicates", candidates: body.duplicates };
  }
  if (!isOrder(body)) {
    throw new ApiError(
      502,
      "We couldn’t start this bond submission. Please try again.",
      body,
    );
  }
  return { kind: "created", submission: body };
}

/** GET /api/broker/bond/submissions/{id} — the order, its captured values, and
 *  any settled step runs (so a reload restores the flow, incl. `referred`). */
export function getBondSubmission(
  request: AuthedRequester,
  id: string,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${BASE}/submissions/${encodeURIComponent(id)}`,
  );
}

// ─── Steps (save / run) ───────────────────────────────────────────────────────

/**
 * The broker's answer to "has any of this information changed?" on a REPEAT
 * request (BRK-S162, consuming PMS-S462).
 *
 * TWO BOOLEANS rather than one enum, because the underwriter cares about the
 * indemnitor half on its own: a changed indemnitor likely means a fresh GIA, and
 * PMS alerts on exactly that flag. The four ANSWERS a broker picks from, and
 * which configuration steps each covers, are `lib/bondRepeatRequest.ts`'s — this
 * is the wire shape and nothing else.
 */
export interface ChangeDeclaration {
  business: boolean;
  indemnitors: boolean;
}

export interface SaveBondStepInput {
  values: Record<string, unknown>;
  /**
   * Whether the broker's edits also update the ACCOUNT record, not just this bond
   * order. Offered per fields step in `existing` mode only (default checked);
   * `new` mode omits it — there is no prior record to diverge from.
   */
  pushToAccount?: boolean;
  /**
   * WHO the people on this step are (contract 3.0.0 / SUR-S065, adopted in
   * BRK-S152) — sent only by a ROSTER step, which is the only surface in this
   * portal that knows about more than one of them.
   *
   * The roster is DECLARED BY THE CALLER, and that is what makes an identity
   * exist before the first keystroke: discovery merges declarations by
   * `ownerId`, so a save carries only the people its own screen knows about.
   * Every repeated value on the same save is keyed by one of these ids.
   */
  owners?: BondOwnerDeclaration[];
  /**
   * The broker's answer to "has any of this information changed?" on a REPEAT
   * request (BRK-S162, consuming PMS-S462).
   *
   * ⚠️ READ AHEAD OF THE PIN. PMS-S462 is in backlog; it stores this on the
   * bond_submission POINTER (not discovery) and, when `indemnitors` is true,
   * alerts an underwriter, because a changed indemnitor likely means a fresh
   * GIA. Sent only by the repeat spine's Summary chip, so an ordinary order's
   * saves are byte-identical to today.
   *
   * ORDER-LEVEL, not step-level, although it rides on a step save: the step
   * save is the only write the broker BFF offers between an order's creation
   * and its submit, and the declaration is a fact about the order rather than
   * about the step whose route carries it.
   */
  changeDeclaration?: ChangeDeclaration;
}

/**
 * One person as this portal DECLARES them on a save — the roster entry minus
 * what discovery reconciles (it owns `elementId`, the vendor's row id, and
 * nothing here addresses anybody by it).
 *
 * A declaration naming a primary DEMOTES the others upstream, and two primaries
 * in one declaration is a 422 rather than a guess — so exactly one entry may
 * carry `isPrimary: true`.
 */
export interface BondOwnerDeclaration {
  ownerId: string;
  displayName: string;
  isPrimary: boolean;
}

// ─── Repeated-block values, keyed by the roster (contract 3.0.0 / SUR-S065) ──

/**
 * ONE element of a repeated block, addressed by the OWNER ROSTER's identity.
 *
 * A `block[].field` path carries a LIST of these and NEVER a positional list of
 * bare values, which is the contract's own hardest-won rule: the positional
 * echo DROPPED HOLES — owner 1 omits a date of birth, owner 2 supplies one, and
 * owner 2's value arrives at index 0 to be read as owner 1's. A read surface
 * attributing a date of birth or a home address to the wrong person is the
 * worst kind of quiet defect, because nothing errors and the value is plausible
 * where it lands.
 *
 * An element that did not supply the field simply HAS NO ENTRY. There is no
 * positional null in the hole: an explicit `null` is reserved service-wide for
 * "clear", so a null in somebody's slot would read as an instruction.
 */
export interface BondElementValue {
  ownerId: string;
  value: unknown;
}

/**
 * The `{ownerId, value}` entries of a repeated path's value, or **null** when
 * the value is not an entry list at all.
 *
 * Null is the honest answer for a value that is something else — a scalar PMS
 * resolved to the primary owner (which is what its broker view does today, see
 * `bondRoster.ts`), or a legacy positional capture. Stored orders are never
 * rewritten, so both survive forever and the caller decides rather than being
 * handed a guess. NEVER positional: a caller that cannot name the owner must
 * leave the value out, not attribute it.
 *
 * Mirrors PMS's `elementEntries` verbatim, which mirrors discovery's.
 */
export function elementEntries(value: unknown): BondElementValue[] | null {
  if (!Array.isArray(value)) return null;
  const entries: BondElementValue[] = [];
  for (const raw of value) {
    if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
      return null;
    }
    const keys = Object.keys(raw);
    if (
      keys.length !== 2 ||
      !keys.includes("ownerId") ||
      !keys.includes("value")
    ) {
      return null;
    }
    const { ownerId, value: held } = raw as Record<string, unknown>;
    if (typeof ownerId !== "string" || ownerId === "") return null;
    entries.push({ ownerId, value: held });
  }
  return entries;
}

/**
 * One named person's entry, or `undefined` because they answered nothing.
 *
 * Undefined is a real answer and not a gap to fill: a person with no entry
 * supplied no value, and the neighbouring entry belongs to somebody else.
 */
export function entryForOwner(
  entries: readonly BondElementValue[],
  ownerId: string,
): BondElementValue | undefined {
  return entries.find((entry) => entry.ownerId === ownerId);
}

/**
 * What a pushing save actually did to the insured's ACCOUNT record (PMS-S229).
 *
 * The three lists mean different things and must never be flattened into one
 * "not saved" bucket:
 *   • `applied`  — verified PRESENT on the stored account afterwards. Since
 *                  PMS-S229 this is read back from persistence, not inferred
 *                  from what the mapper intended, so it can be trusted.
 *   • `unmapped` — has no account home BY DESIGN. Bare paths: the wire carries
 *                  NO reason for these, and this client must not invent one
 *                  (that would hardcode PMS's mapping rationale into the SPA
 *                  and go stale silently the next time it changes).
 *   • `skipped`  — mapped and ATTEMPTED, but absent afterwards. A defect
 *                  report, empty in normal operation. Never tell the broker
 *                  one of these was saved.
 */
export interface BondAccountPushReport {
  /** Whether a push was attempted at all — false when the broker unchecked it. */
  pushed: boolean;
  updated: boolean;
  applied: string[];
  unmapped: string[];
  /** OPTIONAL on the wire (`default: []`) — never assume it is present. */
  skipped?: Array<{ path: string; reason: string }>;
}

/** A step save's response: the order, plus what the push did to the account. */
export interface BondStepSaveResult {
  submission: BondSubmission;
  account: BondAccountPushReport;
}

/** PUT /api/broker/bond/submissions/{id}/steps/{stepKey} — persist one step.
 *  PMS answers with the order's fields AND an `account` push report alongside
 *  them; both are returned so the flow can tell the broker what actually
 *  landed on the insured's record rather than staying silent (BRK-S101). */
export async function saveBondStep(
  request: AuthedRequester,
  id: string,
  stepKey: string,
  input: SaveBondStepInput,
): Promise<BondStepSaveResult> {
  const body = await request<
    BondSubmission & { account?: BondAccountPushReport }
  >(
    `${BASE}/submissions/${encodeURIComponent(id)}/steps/${encodeURIComponent(stepKey)}`,
    { method: "PUT", body: input },
  );
  const { account, ...submission } = body;
  return {
    submission: submission as BondSubmission,
    // A response without the report is treated as "no push claim made" rather
    // than as an empty push — absent is not the same as pushed-and-nothing-took.
    account: account ?? {
      pushed: false,
      updated: false,
      applied: [],
      unmapped: [],
    },
  };
}

/** How a settled Lendflow orchestration run came out. Anything other than `pass`
 *  refers the order to underwriting — advisory, never blocking. */
export type BondRunOutcome = "pass" | "fail" | "refer" | "error";

/**
 * One service's result within a data-pull run (a row in the checks list).
 *
 * `queued` is the state a pull is ACKNOWLEDGED in (SUR-S071, adopted in
 * BRK-S112): the run exists, is named and is pollable, but the vendor has not
 * been asked to do anything yet.
 *
 * It USED to render exactly like `pending`, on the reasoning that discovery's
 * scheduling is not something a broker can act on. Observed live on
 * 2026-08-20, that was the defect: a step held two passed pulls, one at the
 * vendor and THREE THAT HAD NEVER BEEN SENT, and this page said the same thing
 * about all of them. `checkState` below is where the three are told apart now,
 * and PMS-S187 is what made `queued` reach us in the first place.
 */
export interface BondRunCheck {
  key: string;
  title: string;
  /** What the check is doing / found — the row's supporting line. */
  detail?: string | null;
  status: "queued" | "pending" | "pass" | "fail" | "refer" | "error";
  /** The underwriting rule behind the check, shown as an amber chip when the
   *  server supplies one. */
  logic?: string | null;
  /** Lendflow's own numeric run id, stringified — for correlating with the
   *  vendor console. Null while the row is queued: there is no vendor run yet.
   *  Never an address for anything this portal calls (SUR-S071). */
  vendorRunId?: string | null;
  /** Why this pull failed, when anything is known — the vendor's reason for a
   *  refused execute, or a run that ended with no outcome at all. Null on a
   *  healthy row, and RENDERED where it is not: a failed row a broker cannot
   *  explain is a support call (SUR-S071). */
  error?: string | null;
}

/**
 * Has this row reached a verdict?
 *
 * The one place `queued` and `pending` are folded together — they are both
 * NON-TERMINAL, and neither is an answer. ABSENCE IS NOT A VERDICT: a row
 * without one must never render as a pass, a fail, or a zero-state that reads
 * like one (BRK-S112).
 *
 * What it deliberately no longer decides is HOW a non-verdict row reads. That
 * is `checkState`.
 */
export function hasVerdict(status: BondRunCheck["status"]): boolean {
  return status !== "queued" && status !== "pending";
}

/**
 * The THREE things a row can be telling a broker (PMS-S187).
 *
 *   • `verdict`         — the pull answered; show the outcome;
 *   • `awaiting_vendor` — SENT, and the bureau has not answered;
 *   • `not_sent`        — named, and nothing has been asked of any vendor.
 *
 * `not_sent` is the one that was invisible, and it is the most important of the
 * three: it is the only one that tells a broker the wait is not about their
 * business.
 *
 * THE DISCRIMINATOR IS `vendorRunId`, not `status`, and this is not belt and
 * braces. `vendorRunId` is null exactly until the run reaches Lendflow, and
 * BRK has carried the field since BRK-S112 — so this reads the same three
 * states out of a PMS that predates PMS-S187, where the in-flight fold to
 * `pending` is all the status says. That is the whole of the fallback: no
 * branch on a deploy window, no feature test, and the fold path below stays
 * exactly as it was.
 *
 * `status === "queued"` is honoured first all the same. Once PMS states the
 * fact outright, a row that says so is not second-guessed.
 */
export type BondRunCheckState = "verdict" | "awaiting_vendor" | "not_sent";

export function checkState(check: BondRunCheck): BondRunCheckState {
  if (hasVerdict(check.status)) return "verdict";
  if (check.status === "queued") return "not_sent";
  // A `pending` row with no vendor run id has not been sent, whatever a PMS
  // that folds the two in-flight states calls it.
  return check.vendorRunId === null || check.vendorRunId === undefined
    ? "not_sent"
    : "awaiting_vendor";
}

/**
 * A data-pull run. `status` is the RUN's progress; `outcome` is null until it
 * settles. A settled non-`pass` outcome sets the order's sticky `referred` flag
 * server-side — the broker always completes the flow either way.
 */
/**
 * A step's data-pull state.
 *
 * A step may run SEVERAL orchestrations (SUR-S051). PMS hands us BOTH: one
 * `checks` row PER ORCHESTRATION, and a fold of them on the scalar `status` and
 * `outcome` (PMS-S221, corrected by PMS-S187).
 *
 *   • `checks` IS THE TRUTH, and since PMS-S187 the fold is DERIVED from it
 *     server-side — so the two cannot disagree, which is the only arrangement
 *     in which publishing both is safe. Rows are keyed per run when there are
 *     several, so two cannot collapse in a keyed renderer, and each row's state
 *     is its own: read it with `checkState`, never off the fold.
 *   • `status` is `settled` only when EVERY run has settled, and `outcome` is
 *     their aggregate. Both are the server's decision, not ours — there is
 *     nothing here to re-derive across runs, and `isSettled`/`isReferral` read
 *     the fold exactly as they read a single run.
 *
 * THIS COMMENT USED TO SAY "PMS does not hand us the list", and that claim
 * outlived the code by two stories. Believing it is what left the Checks step
 * rendering a step's rows off the fold: on 2026-08-20 a stalled step showed six
 * identical spinning rows while PMS was already publishing two passes, one pull
 * at the vendor and three never sent. The rows were there the whole time; what
 * was missing was the difference between the last two, which PMS-S187 adds.
 *
 * So a multi-bureau step needed no new polling and no branch on length: a
 * one-orchestration step is a fold of one, and keeps the step's own key + title.
 */
export interface BondStepRun {
  /**
   * The first run's id — the fold's identity, kept for a single-run step.
   *
   * An OPAQUE service id since SUR-S071 (BRK-S112): it names discovery's own
   * run, not Lendflow's numeric one, because a run is acknowledged before the
   * vendor has issued anything to name it and an id that changed mid-poll would
   * break the consumer holding it. Nothing here may assume its shape.
   */
  runId: string;
  /** Every run this step started, one per orchestration — opaque service ids,
   *  as `runId` is. Required by the contract: a fold of one still names its
   *  single run. */
  runIds: string[];
  stepKey: string;
  /** `queued` is the fold of a step whose pulls are all acknowledged and none
   *  sent (SUR-S071, published by PMS-S187). Non-terminal, like `running`:
   *  `isSettled` is what the poller and the UI ask, and only `settled` answers
   *  it. A PMS that predates PMS-S187 says `running` here instead, which is
   *  why nothing branches on this to decide a ROW's state — the rows say it. */
  status: "queued" | "pending" | "running" | "settled";
  outcome: BondRunOutcome | null;
  checks: BondRunCheck[];
  /** Why the run referred/failed, when the server explains it. */
  reasons?: string[];
}

/** Whether a run has settled (its outcome is final). */
export function isSettled(run: BondStepRun): boolean {
  return run.status === "settled";
}

/** Whether a SETTLED run refers the order to underwriting. An unsettled run is
 *  not a referral — `outcome` is still null. */
export function isReferral(run: BondStepRun): boolean {
  return isSettled(run) && run.outcome !== null && run.outcome !== "pass";
}

/**
 * WHAT the flow is waiting on, in a broker's words — for the timeout notice
 * (PMS-S187).
 *
 * "The check is taking longer than expected" was true of any of six rows and
 * said nothing about which, which is exactly the complaint the 2026-08-20
 * stall produced: two of those checks had passed and three had never been sent.
 * The rows now say which, so the notice can too.
 *
 * Null when there is nothing to name — no run yet, or a run whose rows have all
 * settled — because a sentence naming nothing is worse than the generic one.
 */
export function waitingOnCheck(run: BondStepRun | null): string | null {
  if (run === null) return null;
  const atVendor = run.checks.find((c) => checkState(c) === "awaiting_vendor");
  if (atVendor) return atVendor.title;
  // Nothing at a vendor, but something still unsent: the honest answer is the
  // FIRST unsent row, because it is the one everything behind it waits on.
  const unsent = run.checks.find((c) => checkState(c) === "not_sent");
  return unsent ? unsent.title : null;
}

/**
 * The timeout notice's whole sentence. Two shapes, because "sent and quiet" and
 * "never sent" are different news for a broker — the second says the wait is
 * not about their business at all.
 *
 * The reassurance is identical in both: a bond order is NEVER blocked by a
 * pull, and the one thing the broker needs to know is that they can carry on.
 */
export function slowCheckMessage(run: BondStepRun | null): string {
  const carryOn = "You can keep going and we’ll finish it in the background.";
  const name = waitingOnCheck(run);
  if (name === null) {
    return `The check is taking longer than expected. ${carryOn}`;
  }
  const unsent =
    run !== null &&
    !run.checks.some((c) => checkState(c) === "awaiting_vendor");
  return unsent
    ? `${name} hasn’t started yet — it’s waiting behind the other checks. ${carryOn}`
    : `${name} is taking longer than expected. ${carryOn}`;
}

/** POST /api/broker/bond/submissions/{id}/steps/{stepKey}/run — start the pull. */
export function runBondStep(
  request: AuthedRequester,
  id: string,
  stepKey: string,
): Promise<BondStepRun> {
  return request<BondStepRun>(
    `${BASE}/submissions/${encodeURIComponent(id)}/steps/${encodeURIComponent(stepKey)}/run`,
    { method: "POST" },
  );
}

/** GET …/steps/{stepKey}/run — read the run's current state (the poll read). */
export function getBondStepRun(
  request: AuthedRequester,
  id: string,
  stepKey: string,
): Promise<BondStepRun> {
  return request<BondStepRun>(
    `${BASE}/submissions/${encodeURIComponent(id)}/steps/${encodeURIComponent(stepKey)}/run`,
  );
}

/** How long to wait between polls, and how long to keep trying. */
export interface PollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  /** Called with each intermediate read, so the UI can show progress. */
  onUpdate?: (run: BondStepRun) => void;
  /** Injected in tests; defaults to a real timer. */
  wait?: (ms: number) => Promise<void>;
}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Poll a data-pull run until it settles, resolving with the settled run.
 *
 * Rejects with an `ApiError` on timeout so the caller renders one handled retry
 * state for "it timed out" and "the read failed" alike (NN #7) — a bond order is
 * never blocked by a pull, so the UI's recovery is the same either way.
 */
export async function pollBondStepRun(
  request: AuthedRequester,
  id: string,
  stepKey: string,
  options: PollOptions = {},
): Promise<BondStepRun> {
  const {
    intervalMs = 1500,
    timeoutMs = 90_000,
    onUpdate,
    wait = sleep,
  } = options;
  const deadline = Date.now() + timeoutMs;
  // Poll at least once regardless of the budget, so a zero/expired timeout still
  // reports the run's real state rather than a timeout it never checked.
  for (;;) {
    const run = await getBondStepRun(request, id, stepKey);
    onUpdate?.(run);
    if (isSettled(run)) return run;
    if (Date.now() >= deadline) {
      // NAMES the check (PMS-S187). The run in hand is the last read, so its
      // rows are the freshest statement of what the wait is actually on.
      throw new ApiError(504, slowCheckMessage(run));
    }
    await wait(intervalMs);
  }
}

// ─── Watching a customer-connection link ─────────────────────────────────────

/** How often to re-read the order, and where to send each fresh link. */
export interface BondLinkWatchOptions<L> {
  /** Default 12s. The customer's side of this takes minutes, not seconds. */
  intervalMs?: number;
  /** Called with each link the order reports, INCLUDING the terminal one. */
  onUpdate: (link: L) => void;
  /** Injected in tests; defaults to a real timer. */
  wait?: (ms: number) => Promise<void>;
}

/** The two things that differ between the accounting card's watch and the bank
 *  card's — which link to follow, and when to stop. */
interface BondLinkWatchSubject<L> {
  /**
   * The link this watch follows, or `undefined` for a read that says NOTHING
   * about it. Both meanings of "nothing" collapse here on purpose: an absent
   * key, and — for the bank link — a key carrying no `state`. Neither is news,
   * and reporting either would make a card that is rendering a real state
   * disappear out from under the broker.
   */
  readonly select: (order: BondSubmission) => L | undefined;
  /** Whether the loop's work is done. */
  readonly settled: (link: L) => boolean;
}

/**
 * Re-read a filed order until one of its customer-connection links SETTLES,
 * reporting each state on the way (BRK-S119, generalised to both links in
 * BRK-S121).
 *
 * ONE LOOP, TWO CARDS. The accounting link and the bank link differ in exactly
 * two ways — which key to read and which state means done — and both are
 * arguments here. Everything else is identical and none of it is obvious: wait
 * before the first read, swallow a failed read, skip a read that says nothing,
 * report the terminal state and then stop, and cancel on demand. A second
 * hand-written loop for the bank card would have had to re-derive all five, and
 * the copy that drifted would have been the failure handling — the one part
 * nobody looks at until a BFF hiccup blanks a card in front of a broker.
 *
 * A SUBSCRIPTION, not an await — which is the whole reason this is not
 * `pollBondStepRun` with different arguments. That poller exists to hand a
 * caller one settled run: it resolves, it times out, and the flow waits on it
 * because the broker is standing in front of a spinner. Nothing waits on this
 * one. The panel already has a state to render from the moment it mounts; the
 * poll only ever makes an already-correct card fresher. So it hands back a
 * CANCEL function and reports through `onUpdate`, and the caller's unmount is
 * what ends it.
 *
 * WAITS FIRST, READS SECOND. The caller was handed the seed by the response
 * that rendered the panel — the submit's own body, or the filed order's read —
 * so an immediate re-read would ask for something it already has.
 *
 * A FAILED READ IS NOT NEWS. Transient BFF hiccups are normal, and the last
 * known state is still the best answer anyone has: the loop swallows the error,
 * reports nothing, and tries again on the next tick. Blanking the panel or
 * raising an error would trade a slightly stale truth for a visible lie — and
 * on a card whose whole point is "there is nothing for you to do", an alarm the
 * broker cannot act on is worse than silence (NN #7).
 *
 * THERE IS NO DEADLINE, deliberately. `pollBondStepRun` needs one because a
 * promise nobody settles is a UI that never moves; here the loop's own stop
 * condition is the link settling, and an unmount ends it besides. A deadline
 * would instead give up on a link that is still legitimately pending while the
 * broker is looking straight at it.
 */
function watchBondLink<L>(
  request: AuthedRequester,
  id: string,
  options: BondLinkWatchOptions<L>,
  subject: BondLinkWatchSubject<L>,
): () => void {
  const { intervalMs = 12_000, onUpdate, wait = sleep } = options;
  let cancelled = false;
  void (async () => {
    for (;;) {
      await wait(intervalMs);
      if (cancelled) return;
      let link: L | undefined;
      try {
        link = subject.select(await getBondSubmission(request, id));
      } catch {
        // Keep the last-known state and try again on the next tick.
        continue;
      }
      if (cancelled) return;
      if (link === undefined) continue;
      onUpdate(link);
      if (subject.settled(link)) return;
    }
  })();
  return () => {
    cancelled = true;
  };
}

/** The accounting card's watch: `accountingLink`, until it settles. */
export type AccountingLinkWatchOptions =
  BondLinkWatchOptions<BondAccountingLink>;

/**
 * Watch the ACCOUNTING link on a filed order (BRK-S119).
 *
 * An ABSENT link on a later read is not news — the key is absent when no
 * accounting step was declared, which cannot become true mid-order — so it is
 * skipped rather than reported.
 */
export function watchAccountingLink(
  request: AuthedRequester,
  id: string,
  options: AccountingLinkWatchOptions,
): () => void {
  return watchBondLink(request, id, options, {
    select: (order) => order.accountingLink,
    settled: (link) => isAccountingLinkSettled(link.state),
  });
}

/** The bank card's watch: `bankLink`, until its figures land. */
export type BankLinkWatchOptions = BondLinkWatchOptions<BondBankLink>;

/**
 * Watch the BANK link on a filed order (BRK-S121).
 *
 * TWO KINDS OF NOTHING ARE SKIPPED HERE, not one. An absent `bankLink` is the
 * accounting watch's case — there is nothing to hand over, and that cannot
 * become true mid-order. A `bankLink` with NO `state` is the second: it means an
 * order minted before contract 5.5.0, so nothing is known about the customer's
 * progress, and reporting it would replace a real status line with silence — the
 * card going backwards under a broker who was reading it.
 *
 * The consequence is deliberate: a watch only ever runs for a link that already
 * HAS a state (the hook does not start one otherwise), and a stateless read
 * cannot end it.
 */
export function watchBankLink(
  request: AuthedRequester,
  id: string,
  options: BankLinkWatchOptions,
): () => void {
  return watchBondLink(request, id, options, {
    select: (order) =>
      order.bankLink?.state === undefined ? undefined : order.bankLink,
    settled: (link) =>
      link.state !== undefined && isBankLinkSettled(link.state),
  });
}

// ─── Submit ───────────────────────────────────────────────────────────────────

/** POST /api/broker/bond/submissions/{id}/submit — file the order. `data` is
 *  the final step's project-&-bond bag: it rides on the submit body and the
 *  server validates it against the order's config snapshot (the terms travel
 *  with the filing, not as a step save). Returns the order carrying its real
 *  SB-##### number for the confirmation screen. */
export function submitBond(
  request: AuthedRequester,
  id: string,
  data: Record<string, unknown>,
  /**
   * The Signal Advance answer, when the broker gave one before filing
   * (BRK-S161). OMITTED when they did not: a `declined` this portal invented
   * would record a refusal nobody gave, and PMS relays the key verbatim.
   */
  advanceOptIn?: BondAdvanceOptIn,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${BASE}/submissions/${encodeURIComponent(id)}/submit`,
    {
      method: "POST",
      body: { data, ...(advanceOptIn ? { advanceOptIn } : {}) },
    },
  );
}

/**
 * Did the broker ENROL? The two opt-in answers, and `declined` is not one.
 *
 * One place, because it is the rule that decides whether the connect-bank and
 * connect-accounting links are handed over — and a second reading of it would
 * eventually hand them to somebody who said no.
 */
export function isAdvanceEnrolled(
  answer: BondAdvanceOptIn | null | undefined,
): boolean {
  return answer === "this_project" || answer === "future_projects";
}

/**
 * POST …/advance-opt-in — answer the Signal Advance offer AFTER filing
 * (PMS-S435, adopted in BRK-S161).
 *
 * The offer itself rides the submit body. This route is for the two cases that
 * bring a broker back: an order filed before the offer existed, and a broker
 * who declined and then saw what their Signal Express limits came out at. Both
 * reach the program from the ACCOUNT page, where there is no submit to attach
 * an answer to.
 *
 * Answers **409 when the order is already enrolled** — the refusal is about the
 * order's current ANSWER, not about its status, so `declined` and never-asked
 * both go through. Re-scoping or withdrawing an enrolment is deliberately not
 * offered: consent that has already connected a bank feed is unwound by Signal,
 * not by a second POST.
 *
 * Resolves the REFRESHED order view, because the enrolment is what makes
 * discovery mint the links — so the caller renders them without a second read.
 *
 * ⚠️ THE BODY'S SPELLING IS IN DISPUTE, AND IT IS SPELLED HERE ONCE.
 * `docs/openapi.yaml` on PMS's `PMS-S435-signal-advance-opt-in` branch — the
 * bytes this repo's pin was taken from, at `f019a6e` — declares
 * `{ advanceOptIn }` with all three values, `declined` included. Aaron's
 * coordinating session reported the as-built shape as `{ choice }` with
 * `declined` NOT accepted here. BRK follows the PUBLISHED contract, which is
 * this repo's standing rule and the one the drift guard can actually check;
 * the disagreement is raised rather than guessed at, and if the route really
 * takes `{ choice }` this function is the ONE line that moves.
 *
 * `declined` matters on this route specifically: it is what makes "asked and
 * said no" a different fact from "never asked" (`null`), and a route that
 * refused it would leave the decline unrecordable from the one surface that
 * offers it.
 */
export function setBondAdvanceOptIn(
  request: AuthedRequester,
  id: string,
  advanceOptIn: BondAdvanceOptIn,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${BASE}/submissions/${encodeURIComponent(id)}/advance-opt-in`,
    { method: "POST", body: { advanceOptIn } },
  );
}

// ─── The Program Indication letter (BRK-S133) ────────────────────────────────

/**
 * GET …/indication.pdf — the Program Indication letter's BYTES (own-scoped;
 * PMS answers 404 until one exists, which is what `indicationAvailable`
 * predicts).
 *
 * Takes the BLOB requester, not the JSON one. Two reasons, and both are the
 * kind that produce an empty frame rather than an error: `apiRequest` would
 * `JSON.parse` a PDF, and a plain `<iframe src>` cannot fetch this at all —
 * the route is bearer-authenticated and an iframe's own request carries no
 * `Authorization` header. So the SPA fetches the bytes and hands the viewer an
 * object URL (`useIndicationPdf`).
 */
export function getBondIndicationPdf(
  fetchBlob: AuthedBlobRequester,
  id: string,
): Promise<FileResponse> {
  return fetchBlob(
    `${BASE}/submissions/${encodeURIComponent(id)}/indication.pdf`,
  );
}

/** The cap and the type PMS enforces on the upload, restated so the broker
 *  learns before the wait rather than after it. 32 MB, PDF only — the intake
 *  pipeline's own guards, which this route reuses. */
export const FINAL_CONTRACT_MAX_BYTES = 32 * 1024 * 1024;
export const FINAL_CONTRACT_ACCEPT = "application/pdf";

/**
 * POST /api/broker/bond/submissions/{id}/final-contract — the signed contract
 * on a WON bid. Answers **201** with the stored document's POINTER.
 *
 * `multipart/form-data` with ONE `file` part, which is upstream's own choice
 * and worth knowing here: the intake pipeline presigns because the browser
 * holds the bytes for a large scan-heavy packet, but a presign+confirm here
 * would need the document ROW to exist before its object does — and that table
 * has no pending state (`storage_key`, `byte_size` and `version` are all NOT
 * NULL, with the version drawn at write time under a unique constraint).
 *
 * IT RETURNS A POINTER, NOT THE ORDER. The document belongs to the insured's
 * ACCOUNT (`provenance.sourceBondSubmissionId` names this order), so the bytes
 * are fetched through the account's own download route and the ORDER's
 * `finalContract` is refreshed by re-reading the order. A caller that treated
 * this response as an order would render an order with no number.
 *
 * A re-upload APPENDS a new version rather than replacing; the order view
 * publishes the latest. Needs `submission:edit` AND `account:edit`, because
 * this writes a durable record onto the insured rather than only onto the bond
 * order.
 */
export function uploadBondFinalContract(
  upload: AuthedUploader,
  id: string,
  file: File,
): Promise<BondFinalContract> {
  const form = new FormData();
  form.append("file", file);
  return upload<BondFinalContract>(
    `${BASE}/submissions/${encodeURIComponent(id)}/final-contract`,
    form,
  );
}

/** Whether a failed submit is "this order was already submitted" (PMS 409) — a
 *  double-submit the UI treats as success-already-happened, not an error. */
export function isAlreadySubmitted(err: unknown): boolean {
  // NOT every 409 since BRK-S181: a write on a CANCELLED order is a 409 too,
  // and reading it as "already filed" would show the broker a confirmation for
  // an order that was discarded. Same for a cancel refused on a decision.
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    !isOrderCancelled(err) &&
    !isOrderDecided(err)
  );
}

// ─── Cancelling a pre-approval in flight (BRK-S181, PMS-S479) ────────────────
//
// The route and both codes are PMS-S479's; adopted ahead of it and pinned since
// the re-pin onto rev/PMS-S479 @ 6a5e4bc (`test/lib/bondCancelContract.test.ts`).

/** 409 `ORDER_CANCELLED` — a write on an order that has been cancelled. */
export function isOrderCancelled(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    apiErrorCode(err) === "ORDER_CANCELLED"
  );
}

/** 409 `ORDER_HAS_DECISION` — a cancel refused because the pre-approval has
 *  already been decided: it is no longer in flight, so there is nothing to
 *  discard. */
export function isOrderDecided(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    apiErrorCode(err) === "ORDER_HAS_DECISION"
  );
}

/**
 * POST …/submissions/{id}/cancel — discard an order, KEEPING it (BRK-S181).
 *
 * No body. Answers the same broker order view the GET does, with status
 * `cancelled`, and is idempotent — a second cancel answers the same view. 409
 * `ORDER_HAS_DECISION` when the pre-approval is already decided; another
 * brokerage's order is a 404, like every other own-scoped read.
 */
export function cancelBondSubmission(
  request: AuthedRequester,
  id: string,
): Promise<BondSubmission> {
  return request<BondSubmission>(
    `${BASE}/submissions/${encodeURIComponent(id)}/cancel`,
    { method: "POST" },
  );
}

/**
 * The rows that COULD be a pre-approval in flight (BRK-S181): the pre-approval
 * FLOW, drafted or filed. Cancelled is terminal, and a bid bond (or an absent
 * flow, which is `bond`) is never guarded — a contractor legitimately has
 * several bid bonds in progress at once, for different jobs.
 *
 * ⚠️ THE ROW'S `decision` IS NOT READ, and must not be. PMS folds that column
 * from the INSURED'S standing program, not from the order: a revoked program
 * reads every order "referred" and an approved one reads every order
 * "approved", so a guard that trusted it treated unfinished drafts as decided
 * (Aaron's SB-01613, 2026-09-22). Whether a candidate is decided is the ORDER
 * VIEW's to say — `isPreapprovalInProgress`.
 */
export function preapprovalCandidates(
  rows: readonly BondSubmissionListItem[],
): BondSubmissionListItem[] {
  return rows.filter(
    (row) =>
      bondFlowOf(row) === "preapproval" &&
      (row.status === "draft" || row.status === "submitted"),
  );
}

/**
 * Is this pre-approval order still IN FLIGHT, by its OWN order view (BRK-S181)?
 *
 * Aaron's rule, 2026-09-22: "in progress" is ANY NON-TERMINAL pre-approval
 * order — no decision yet, not merely a draft — so a filed order whose checks
 * are running counts. Read off the order's own `preapproval` block:
 *
 *   • `pending`, or no block published yet → in progress;
 *   • `approved` / `not_granted` from THIS order's review → decided;
 *   • `source: account` is the INSURED's standing grant, not this order's
 *     decision — the order ran nothing. A DRAFT carrying it is unfinished, so
 *     it is in progress (discovery would still cancel it); a FILED one is
 *     complete against it.
 *
 * Cancelled is terminal, whatever the block says.
 */
export function isPreapprovalInProgress(
  order: Pick<BondSubmission, "status" | "preapproval">,
): boolean {
  if (order.status !== "draft" && order.status !== "submitted") return false;
  const block = order.preapproval;
  if (block == null || block.status === "pending") return true;
  if (block.source === "account") return order.status === "draft";
  return false;
}

/**
 * Every in-flight pre-approval order for one insured (BRK-S181).
 *
 * The existing list, narrowed server-side by `accountId` and read to the END,
 * finds the candidates — all pages, because the one order that matters could
 * be on the second. Then each candidate's ORDER VIEW decides, read in parallel:
 * there are typically none to two, so the fan-out is bounded. PMS excludes
 * cancelled orders from the default list already; the filter re-checks it
 * anyway, because the rule is stated here.
 *
 * Any read failing fails the whole answer — the caller creates nothing rather
 * than guess, since guessing "none" is how a second live order gets made.
 */
export async function findPreapprovalsInProgress(
  request: AuthedRequester,
  accountId: string,
  pageSize = 100,
): Promise<BondSubmissionListItem[]> {
  const candidates: BondSubmissionListItem[] = [];
  let offset = 0;
  // Bounded, so a server that miscounts `total` cannot loop this forever.
  for (let pages = 0; pages < 50; pages += 1) {
    const page = await listBondSubmissions(request, {
      accountId,
      limit: pageSize,
      offset,
    });
    candidates.push(...preapprovalCandidates(page.items));
    offset += page.items.length;
    if (page.items.length === 0 || offset >= page.total) break;
  }
  const views = await Promise.all(
    candidates.map((row) => getBondSubmission(request, row.id)),
  );
  return candidates.filter((_, i) => isPreapprovalInProgress(views[i]));
}

// ─── Documents attached during intake (BRK-S145, PMS-S334) ───────────────────

/**
 * A document as it appears IN A STEP'S VALUE BAG — the reference PMS hands back
 * for one uploaded file.
 *
 * Four fields and no more, deliberately: this is what gets written into the
 * order's captured values and travels on the submit body, so it must be exactly
 * what PMS accepts back. `type` and `fieldKey` and `uploadedAt` are on the
 * fuller `BondDocument` (below) and are the SERVER's to know — echoing them
 * into the bag would be the client restating facts it does not own, and a bag
 * that carried them would be a second, staler copy of the documents list.
 */
export interface BondDocumentRef {
  documentId: string;
  filename: string;
  contentType: string;
  byteSize: number;
}

/**
 * One stored document, as the list and the upload response publish it — SEVEN
 * fields (PMS-S334). No `version`: versioning is per (account, type), so on an
 * order's surface it would number a series the order does not own.
 */
export interface BondDocument extends BondDocumentRef {
  /** PMS's document TYPE key. An open string, labelled from
   *  `GET /api/broker/document-types` rather than branched on — a stored row
   *  whose type this bundle has not heard of must still list and download. */
  type: string;
  /**
   * Which `files` control collected it, or **null** for a document attached to
   * the order by something other than an intake field — the signed contract
   * (PMS-S316), and later the issued bond.
   *
   * NULLABLE and that matters: `GET …/documents` is *the order's documents*,
   * not *the order's intake uploads*, so the list a broker reads back includes
   * files no `files` field ever asked for. A reader that typed this as a string
   * would group the signed contract under a field key of `""`.
   */
  fieldKey: string | null;
  uploadedAt: string;
}

/**
 * POST /api/broker/bond/submissions/{id}/documents — attach ONE file to one
 * configuration field. Answers **201** `{ documents }`.
 *
 * Needs `submission:edit` and deliberately NOT `account:edit` (PMS-S334):
 * these are attachments to an order the broker is filling in, where the
 * final-contract upload is the broker asserting a fact about the insured's file.
 *
 * ONE FILE PER REQUEST, even though the contract's `files` part is plural and
 * the response is a list. Three things fall out of it and all three are the
 * reason: per-file PROGRESS is per-request, a `400 DOCUMENT_REJECTED` names ONE
 * filename (so a batch would leave the client guessing which of five files the
 * rule was about, or worse, discarding four good uploads with it), and a broker
 * dropping six documents watches six rows settle independently rather than one
 * bar that means nothing until it means everything.
 *
 * `type` is OPTIONAL and left unsent: PMS classifies from the field the
 * document answers, and a portal that guessed a type would be inventing
 * metadata about a file it has not read. It stays in the signature because a
 * field that collects one specific document may name it.
 */
export async function uploadBondDocument(
  upload: AuthedProgressUploader,
  id: string,
  input: {
    file: File;
    fieldKey: string;
    type?: string;
    onProgress?: (progress: { loaded: number; total: number }) => void;
  },
): Promise<BondDocument> {
  const form = new FormData();
  form.append("files", input.file);
  form.append("fieldKey", input.fieldKey);
  if (input.type !== undefined) form.append("type", input.type);
  const body = await upload<{ documents: BondDocument[] }>(
    `${BASE}/submissions/${encodeURIComponent(id)}/documents`,
    form,
    { onProgress: input.onProgress },
  );
  const [document] = body.documents;
  if (!document) {
    // A 201 with nothing in it is not a success this client can act on: there
    // is no reference to write into the step value, so treating it as one is
    // how a file reads as attached while the order records nothing (the
    // BRK-S122 defect class, which is what this whole story guards against).
    throw new ApiError(
      201,
      "That file was accepted but no document reference came back — try attaching it again.",
    );
  }
  return document;
}

/**
 * GET …/documents — every document attached to this order, **oldest first**
 * (PMS-S334: "the order of the work, which is how a list of one order's own
 * attachments reads").
 *
 * The array is CHECKED rather than trusted. `documents` is required upstream,
 * so a body without it is drift — and the difference between the two ways of
 * handling that is the whole reason for the check: coercing it to `[]` would
 * tell the broker "nothing was attached", which is a lie about their own
 * record, while reading `.length` off `undefined` white-screens the page a
 * failed sub-read has no business taking down (NN #7). A handled `ApiError`
 * puts the section into its error state and leaves the rest of the record
 * standing. Found by an e2e whose catch-all route answered `{}`.
 */
export async function listBondDocuments(
  request: AuthedRequester,
  id: string,
): Promise<{ documents: BondDocument[] }> {
  const body = await request<{ documents?: BondDocument[] }>(
    `${BASE}/submissions/${encodeURIComponent(id)}/documents`,
  );
  if (!Array.isArray(body.documents)) {
    throw new ApiError(
      200,
      "We couldn’t read this request’s documents — reload the page to try again.",
      body,
    );
  }
  return { documents: body.documents };
}

/**
 * DELETE …/documents/{docId} — detach a document. **204** while the order is a
 * draft, **409** once it is submitted.
 *
 * The 409 is not an error state to render: a submitted order's field is already
 * read-only, so reaching this means the page's own read of the order was stale.
 * The caller re-reads rather than telling the broker off (see `isFiledAlready`).
 */
export function deleteBondDocument(
  request: AuthedRequester,
  id: string,
  documentId: string,
): Promise<void> {
  return request<void>(
    `${BASE}/submissions/${encodeURIComponent(id)}/documents/${encodeURIComponent(documentId)}`,
    { method: "DELETE" },
  );
}

/** PMS's structured refusal of one file: the filename it read and the rule it
 *  broke. Mirrors the `DOCUMENT_REJECTED` details block VERBATIM (PMS-S334) —
 *  one code for all six rules, because the client's recovery is the same: say
 *  which file, and why. */
export interface BondDocumentRejection {
  filename: string;
  rule: DocumentRule;
}

/**
 * A failed upload read as PMS's own per-file refusal, or null when it was
 * something else (a network drop, a 401, a 500).
 *
 * The distinction is the whole point: a REFUSAL is about the file and belongs
 * beside it in the broker's own words ("this isn't a type we accept"), while a
 * failure is about the request and belongs in the field's error line with a
 * retry. Conflating them either invites a pointless retry of a file that will
 * never be accepted, or hides a transient failure behind "bad file".
 *
 * Read defensively — `rule` is validated against the vocabulary, so a rule PMS
 * adds tomorrow reads as "no structured detail" and falls back to the server's
 * own sentence rather than rendering a blank reason.
 *
 * The as-built spelling is **`details.code`** (PMS-S334), the same place every
 * other structured refusal on this surface puts it. A top-level `code` is
 * accepted too: the story brief spelled it that way, it costs one `??`, and a
 * reader that insisted on one spelling would turn a well-formed refusal into a
 * generic failure over a key position.
 *
 * `DOCUMENT_REJECTED` is deliberately NOT added to `ServerErrorCode`: that
 * union is asserted EQUAL to the enum PMS publishes on the bond 4xx responses,
 * so a member this contract has not published yet would fail the guard for a
 * drift that is really an adoption. It is read here as the plain string it is.
 */
export function documentRejection(err: unknown): BondDocumentRejection | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const body = err.body;
  if (typeof body !== "object" || body === null) return null;
  const details = (body as { details?: unknown }).details;
  if (typeof details !== "object" || details === null) return null;
  const code =
    (body as { code?: unknown }).code ?? (details as { code?: unknown }).code;
  if (code !== "DOCUMENT_REJECTED") return null;
  const { filename, rule } = details as { filename?: unknown; rule?: unknown };
  if (typeof filename !== "string" || typeof rule !== "string") return null;
  // Against the mirrored vocabulary as a VALUE — a TS union is erased, so this
  // list is the runtime copy, and it lives beside the type it mirrors.
  if (!(DOCUMENT_RULES as readonly string[]).includes(rule)) return null;
  return { filename, rule: rule as DocumentRule };
}

// ─── List ─────────────────────────────────────────────────────────────────────

/**
 * A row in the brokerage's bond-order list. Derived from the LIVE contract
 * (`GET /api/broker/bond/submissions`): served from PMS's pointer table, not
 * discovery, because the pointer is what knows the number and the owning
 * brokerage. `suretyBondSubmissionId` is the discovery-side id behind the
 * pointer; `id` is the pointer's own.
 */
export interface BondSubmissionListItem {
  id: string;
  number: string;
  accountId: string;
  accountLegalName: string | null;
  bondProductName: string | null;
  bondSubproductName: string | null;
  suretyBondSubmissionId: string;
  status: BondSubmissionStatus;
  referred: boolean;
  createdAt: string;
  /**
   * The row's flow (BRK-S160) — REQUIRED upstream, optional here for the same
   * deploy window as the order view's.
   *
   * On the LIST it does one thing: a submitted pre-approval reads **Complete**
   * rather than "Filed with Signal". Without it the row would have to be
   * resolved against its configuration, which is a second read per row and a
   * second answer to a question the row can state itself.
   */
  flow?: BondFlow;
  /**
   * The bid date the order was filed with, ISO `YYYY-MM-DD` — **null on a
   * draft**, which has no filed bid yet (PMS-S461, read ahead of the pin).
   *
   * Derived by PMS from the order's final-step bag rather than stored on the
   * pointer, so the list says the same date the record does. Displayed
   * `m/d/yyyy` (D-10 rule 2), parsed textually so a date-only value never
   * shifts a day under the viewer's timezone — the classic way a bid date
   * renders as the day before the bid.
   */
  bidDate?: string | null;
  /**
   * Where the request stands, in the only three words a broker may be told
   * (PMS-S461, read ahead of the pin).
   *
   * Derived upstream from the SAME broker-safe facts the order view already
   * publishes — the pre-approval block and the referral flag — and never from
   * anything else. There is no score, no reason and no tier here, now or later:
   * that is appetite, and `test/lib/preapprovalDiscretion.test.ts` sweeps this
   * file for a home for it.
   *
   * **null is a real value**: a draft has had no decision, which is different
   * from `pending` (a decision that has not landed yet). Rendering the two the
   * same way would tell a broker who has not finished filling a request in that
   * somebody is looking at it.
   */
  decision?: BondListDecision | null;
}

/**
 * The decision vocabulary a bond-request row carries (PMS-S461).
 *
 * THREE members and deliberately not the account pre-approval's three: that one
 * answers "does this insured hold a standing program" (`approved` /
 * `not_granted` / `none`), where this answers "where is THIS request". A
 * `referred` request is with an underwriter, which is a fact about the request
 * and not about the insured.
 */
export type BondListDecision = "approved" | "referred" | "pending";

export interface BondListQuery {
  search?: string;
  /**
   * Narrow the list to ONE insured's orders (BRK-S139).
   *
   * Server-side, and additive to the own-scoping rather than a replacement for
   * it: PMS still forces the brokerage from the session, so an account id
   * belonging to somebody else narrows to nothing rather than reaching it. That
   * is what makes it safe to put an id from the URL straight into the query.
   */
  accountId?: string;
  /**
   * Narrow to one lifecycle status (PMS-S461). SERVER-side, like every other
   * filter here: filtering a page of 25 in the client would hide rows the
   * server never sent and call it a filter.
   */
  status?: BondSubmissionStatus;
  /** Narrow to one decision (PMS-S461). Same rule, same reason. */
  decision?: BondListDecision;
  includeInactive?: boolean;
  limit?: number;
  offset?: number;
}

/** GET /api/broker/bond/submissions — the brokerage's own orders, newest first. */
export function listBondSubmissions(
  request: AuthedRequester,
  query: BondListQuery = {},
): Promise<Page<BondSubmissionListItem>> {
  const qs = new URLSearchParams();
  if (query.search) qs.set("search", query.search);
  if (query.accountId) qs.set("accountId", query.accountId);
  if (query.status) qs.set("status", query.status);
  if (query.decision) qs.set("decision", query.decision);
  if (query.includeInactive) qs.set("includeInactive", "true");
  if (query.limit != null) qs.set("limit", String(query.limit));
  if (query.offset != null) qs.set("offset", String(query.offset));
  const s = qs.toString();
  return request<Page<BondSubmissionListItem>>(
    `${BASE}/submissions${s ? `?${s}` : ""}`,
  );
}
